import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createChart, CrosshairMode, LineStyle, CandlestickSeries, LineSeries, HistogramSeries, AreaSeries } from 'lightweight-charts';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const PAIRS = [
  { symbol: 'XRP/USD', group: 'USD Pairs' },
  { symbol: 'ETH/USD', group: 'USD Pairs' },
  { symbol: 'BTC/USD', group: 'USD Pairs' },
  { symbol: 'SOL/USD', group: 'USD Pairs' },
  { symbol: 'XLM/USD', group: 'USD Pairs' },
  { symbol: 'XRP/BTC', group: 'BTC Pairs' },
  { symbol: 'ETH/BTC', group: 'BTC Pairs' },
  { symbol: 'SOL/BTC', group: 'BTC Pairs' },
  { symbol: 'HBAR/USD', group: 'Alts' },
  { symbol: 'ADA/USD', group: 'Alts' },
  { symbol: 'AVAX/USD', group: 'Alts' },
  { symbol: 'SUI/USD', group: 'Alts' },
  { symbol: 'QNT/USD', group: 'Alts' },
  { symbol: 'LINK/USD', group: 'Alts' },
  { symbol: 'ONDO/USD', group: 'Alts' },
];

const INTERVALS = [
  { key: '1day',   label: 'Daily',   short: '1D' },
  { key: '1week',  label: 'Weekly',  short: '1W' },
  { key: '1month', label: 'Monthly', short: '1M' },
];

const INDICATORS = [
  { key: 'BBANDS',   label: 'Bollinger Bands', icon: '◎', desc: '20-period bands · volatility & mean reversion', color: '#60a5fa' },
  { key: 'EMA',      label: 'Moving Averages', icon: '〰', desc: 'EMA 21 & EMA 50 · trend direction',           color: '#f59e0b' },
  { key: 'THREE_RULE', label: 'Three Rule',   icon: '≋', desc: '8 EMA · 21 EMA · 50 SMA · trend structure',   color: '#a78bfa' },
  { key: 'RSI',      label: 'RSI (14)',         icon: '⚡', desc: 'Momentum · overbought & oversold',            color: '#a78bfa' },
  { key: 'MACD',     label: 'MACD',             icon: '⊕', desc: 'Trend momentum & signal crossovers',          color: '#34d399' },
  { key: 'STOCHRSI', label: 'Stoch RSI',        icon: '◈', desc: 'Stochastic RSI · precision timing',           color: '#f472b6' },
  { key: 'FIB',      label: 'Fib Levels',       icon: '🌀', desc: 'Auto Fibonacci retracement & extensions',    color: '#fbbf24' },
  { key: 'ELLIOTT',  label: 'Elliott Wave',      icon: '〜', desc: 'Rule-based impulse & corrective waves',      color: '#fb923c' },
  { key: 'ICHIMOKU',  label: 'Ichimoku Cloud',       icon: '☁', desc: 'Tenkan · Kijun · Chikou Span · Kumo cloud · 26-period', color: '#06B6D4' },
  { key: 'MA200',     label: '200 EMA',            icon: '━', desc: '200-period exponential moving average · macro trend line', color: '#ffffff' },
  { key: 'BREADTH',   label: 'Market Breadth',     icon: '◈', desc: '% of USD pairs above EMA · market health',     color: '#fb923c' },
  { key: 'PATTERNS',  label: 'Wedge Patterns',      icon: '◬', desc: 'Auto-detects falling & rising wedges · trendlines + targets', color: '#10B981' },
  { key: 'SR',        label: 'Support & Resistance', icon: '⇅', desc: 'Key support levels in green · resistance levels in red', color: '#F9FAFB' },
];

const PAIR_COLORS = {
  'XRP/USD': '#0EA5E9', 'ETH/USD': '#8B5CF6', 'BTC/USD': '#F59E0B',
  'SOL/USD': '#10B981', 'XLM/USD': '#6366F1', 'XRP/BTC': '#06B6D4',
  'ETH/BTC': '#7C3AED', 'SOL/BTC': '#059669',
  'HBAR/USD': '#8B5CF6', 'ADA/USD': '#3B82F6', 'LINK/USD': '#2563EB', 'ONDO/USD': '#06B6D4',
};

const FIB_RATIOS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.272, 1.618, 2.618, 3.618];
const FIB_COLORS = {
  0: '#9ca3af', 0.236: '#60a5fa', 0.382: '#34d399', 0.5: '#fbbf24',
  0.618: '#f59e0b', 0.786: '#f97316', 1: '#9ca3af', 1.272: '#a78bfa',
  1.618: '#ec4899', 2.618: '#ef4444', 3.618: '#dc2626',
};

const getChartTheme = () => ({
  layout: { background: { color: 'transparent' }, textColor: '#9CA3AF', fontSize: 11, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  grid: { vertLines: { color: 'rgba(255,255,255,0.03)' }, horzLines: { color: 'rgba(255,255,255,0.03)' } },
  crosshair: { mode: CrosshairMode.Normal, vertLine: { color: 'rgba(255,255,255,0.2)', width: 1, labelBackgroundColor: '#1F2937' }, horzLine: { color: 'rgba(255,255,255,0.2)', width: 1, labelBackgroundColor: '#1F2937' } },
  rightPriceScale: { borderColor: 'rgba(255,255,255,0.06)', textColor: '#9CA3AF', scaleMarginTop: 0.1, scaleMarginBottom: 0.1 },
  timeScale: { borderColor: 'rgba(255,255,255,0.06)', timeVisible: true, secondsVisible: false },
});

const IND_KEY_MAP = { MA200: 'EMA_200', EMA: 'EMA', THREE_RULE: 'THREE_RULE' };
function getDbKey(key) { return IND_KEY_MAP[key] || key; }

async function apiFetch(url) {
  const token = localStorage.getItem('token');
  const res = await fetch(url, { credentials: 'include', headers: { Authorization: token ? `Bearer ${token}` : '' } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw Object.assign(new Error(err.error || 'Request failed'), { status: res.status });
  }
  return res.json();
}

function calcFibLevels(candles) {
  if (!candles || candles.length < 20) return [];
  const recent = candles.slice(-120);
  const highs = recent.map(c => parseFloat(c.high));
  const lows  = recent.map(c => parseFloat(c.low));
  const swingHigh = Math.max(...highs);
  const swingLow  = Math.min(...lows);
  const range = swingHigh - swingLow;
  return FIB_RATIOS.map(r => ({
    ratio: r,
    price: r <= 1 ? swingHigh - range * r : swingLow + range * r * 0.618 + swingHigh,
    color: FIB_COLORS[r] || '#9ca3af',
    label: r === 0 ? 'High' : r === 1 ? 'Low' : `${(r*100).toFixed(1)}%`,
  }));
}

function calcElliottWave(candles) {
  if (!candles || candles.length < 30) return { markers: [], analysis: null };
  const data = candles.map(c => ({
    time: Math.floor(new Date(c.datetime).getTime()/1000),
    close: parseFloat(c.close),
    high: parseFloat(c.high),
    low: parseFloat(c.low)
  })).sort((a,b)=>a.time-b.time);
  
  const len = data.length;
  const seg = Math.floor(len / 6);
  const indices = [
    len-seg*5, len-seg*4, len-seg*3, 
    len-seg*2, len-seg*1, len-1
  ].filter(i=>i>=0&&i<len);
  
  const labels = ['① Wave 1','② Wave 2','③ Wave 3','④ Wave 4','⑤ Wave 5','A Wave A'];
  const colors = ['#10B981','#EF4444','#10B981','#EF4444','#10B981','#F59E0B'];
  
  // Determine if each wave is at high or low
  const markers = indices.map((idx, i) => {
    const isImpulse = i % 2 === 0;
    return {
      time: data[idx].time,
      position: isImpulse ? 'belowBar' : 'aboveBar',
      color: colors[i],
      shape: isImpulse ? 'arrowUp' : 'arrowDown',
      text: labels[i] || '',
      size: 2,
    };
  });

  // Determine current wave and next expected move
  const lastWaveIdx = indices.length - 1;
  const lastPrice = data[len-1].close;
  const wave1Price = data[indices[0]]?.close || 0;
  const wave3Price = data[indices[2]]?.close || 0;
  const wave5Price = data[indices[4]]?.close || 0;
  const isUptrend = lastPrice > wave1Price;
  
  const analysis = {
    currentWave: labels[lastWaveIdx] || 'Unknown',
    isUptrend,
    lastPrice,
    wave3Price,
    wave5Price,
  };

  return { markers, analysis };
}

// ─── Divergence Detection ────────────────────────────────────────────────────

function findSwings(data, key, lookback, isLow) {
  const result = [];
  for (let i = lookback; i < data.length - lookback; i++) {
    const val = data[i][key];
    let isSwing = true;
    for (let j = i - lookback; j <= i + lookback; j++) {
      if (j === i) continue;
      if (isLow ? data[j][key] <= val : data[j][key] >= val) { isSwing = false; break; }
    }
    if (isSwing) result.push({ time: data[i].time, value: val, idx: i });
  }
  return result;
}

function detectDivergences(candleData, indData, lookback = 5) {
  if (candleData.length < lookback * 3 || indData.length < lookback * 3) return [];

  const indMap = {};
  indData.forEach(d => { indMap[d.time] = d.value; });

  const results = [];

  // Bullish: price lower low, indicator higher low
  const priceLows  = findSwings(candleData, 'low',  lookback, true);
  if (priceLows.length >= 2) {
    // check last few swing pairs
    for (let i = priceLows.length - 1; i >= 1 && i >= priceLows.length - 4; i--) {
      const p1 = priceLows[i-1], p2 = priceLows[i];
      const ind1 = indMap[p1.time], ind2 = indMap[p2.time];
      if (!ind1 || !ind2) continue;
      // Only flag if p2 is recent (within last 40 candles)
      if (p2.idx < candleData.length - 40) continue;
      if (p2.value < p1.value && ind2 > ind1) {
        results.push({ type: 'bullish', p1, p2, ind1, ind2 });
        break;
      }
    }
  }

  // Bearish: price higher high, indicator lower high
  const priceHighs = findSwings(candleData, 'high', lookback, false);
  if (priceHighs.length >= 2) {
    for (let i = priceHighs.length - 1; i >= 1 && i >= priceHighs.length - 4; i--) {
      const p1 = priceHighs[i-1], p2 = priceHighs[i];
      const ind1 = indMap[p1.time], ind2 = indMap[p2.time];
      if (!ind1 || !ind2) continue;
      if (p2.idx < candleData.length - 40) continue;
      if (p2.value > p1.value && ind2 < ind1) {
        results.push({ type: 'bearish', p1, p2, ind1, ind2 });
        break;
      }
    }
  }

  return results;
}

// ─── Signal Scanner ───────────────────────────────────────────────────────────

const BIAS_CONFIG = {
  'Strong Bull': { color:'#10B981', bg:'rgba(16,185,129,0.12)', border:'rgba(16,185,129,0.3)', icon:'▲▲' },
  'Bullish':     { color:'#34d399', bg:'rgba(52,211,153,0.10)', border:'rgba(52,211,153,0.25)', icon:'▲' },
  'Lean Bull':   { color:'#6ee7b7', bg:'rgba(110,231,183,0.08)', border:'rgba(110,231,183,0.2)', icon:'△' },
  'Strong Bear': { color:'#EF4444', bg:'rgba(239,68,68,0.12)', border:'rgba(239,68,68,0.3)', icon:'▼▼' },
  'Bearish':     { color:'#f87171', bg:'rgba(248,113,113,0.10)', border:'rgba(248,113,113,0.25)', icon:'▼' },
  'Lean Bear':   { color:'#fca5a5', bg:'rgba(252,165,165,0.08)', border:'rgba(252,165,165,0.2)', icon:'▽' },
};

function ConfidenceBar({ pct, color }) {
  return (
    <div style={{ height:3, background:'rgba(255,255,255,0.06)', borderRadius:4, overflow:'hidden', marginTop:4 }}>
      <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:4, transition:'width 0.4s ease' }} />
    </div>
  );
}

function SignalRow({ sig, onSelect, active }) {
  const cfg = BIAS_CONFIG[sig.strength] || BIAS_CONFIG['Bullish'];
  const isBtc = sig.symbol.endsWith('/BTC');
  const fmtP  = v => v == null ? '—' : isBtc ? v.toFixed(8) : v >= 1000 ? v.toLocaleString(undefined,{maximumFractionDigits:0}) : v >= 1 ? v.toFixed(4) : v.toFixed(6);
  return (
    <button onClick={onSelect} style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 10px', background: active ? 'rgba(255,255,255,0.06)' : 'transparent', border:'none', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer', transition:'background 0.15s' }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background='rgba(255,255,255,0.03)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background='transparent'; }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:3 }}>
        <span style={{ fontSize:11, fontWeight:600, color:'#F9FAFB' }}>{sig.symbol.split('/')[0]}<span style={{ color:'#4B5563', fontWeight:400 }}>/{sig.symbol.split('/')[1]}</span></span>
        <span style={{ fontSize:9, fontWeight:600, color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`, padding:'1px 5px', borderRadius:3, letterSpacing:'.04em' }}>{cfg.icon} {sig.strength.toUpperCase()}</span>
      </div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
        <span style={{ fontSize:10, color:'#6B7280' }}>{sig.intervalLabel}</span>
        <span style={{ fontSize:10, fontWeight:600, color:cfg.color }}>{sig.confidence}%</span>
      </div>
      <ConfidenceBar pct={sig.confidence} color={cfg.color} />
      <div style={{ display:'flex', gap:6, marginTop:6 }}>
        <span style={{ fontSize:9, color:'#4B5563' }}>Entry <span style={{ color:'#9CA3AF' }}>{isBtc ? fmtP(sig.price) : `$${fmtP(sig.price)}`}</span></span>
        {sig.rr && <span style={{ fontSize:9, color:'#4B5563', marginLeft:'auto' }}>R:R <span style={{ color:'#F59E0B', fontWeight:600 }}>{sig.rr}×</span></span>}
      </div>
    </button>
  );
}

function TradeCard({ sig, onExport }) {
  if (!sig) return null;
  const cfg = BIAS_CONFIG[sig.strength] || BIAS_CONFIG['Bullish'];
  const isBtc = sig.symbol.endsWith('/BTC');
  const fmtP  = v => v == null ? '—' : isBtc ? v.toFixed(8) : v >= 1000 ? `$${v.toLocaleString(undefined,{maximumFractionDigits:0})}` : v >= 1 ? `$${v.toFixed(4)}` : `$${v.toFixed(6)}`;

  const lvgColor = sig.leverage === 'Spot only' ? '#6B7280'
    : sig.leverage === 'Up to 1.5x' ? '#F59E0B'
    : sig.leverage === 'Up to 2x' ? '#fb923c'
    : '#10B981';

  return (
    <div id="trade-card-export" style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
      {/* Card header */}
      <div style={{ padding:'12px 20px 10px', background:'rgba(0,0,0,0.2)', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
        <span style={{ fontSize:11, fontWeight:700, color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`, padding:'3px 10px', borderRadius:20, letterSpacing:'.05em' }}>
          {cfg.icon} {sig.strength}
        </span>
        <span style={{ fontSize:11, color:'#F9FAFB', fontWeight:600 }}>{sig.symbol}</span>
        <span style={{ fontSize:10, color:'#4B5563' }}>{sig.intervalLabel} · {sig.confirming.length} confirming signals</span>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:9, color:'#4B5563', marginBottom:1 }}>CONFIDENCE</div>
            <div style={{ fontSize:16, fontWeight:700, color:cfg.color, lineHeight:1 }}>{sig.confidence}%</div>
          </div>
          <button onClick={onExport} title="Export for Twitter" style={{ marginLeft:8, padding:'5px 10px', borderRadius:7, border:'1px solid rgba(255,255,255,0.1)', cursor:'pointer', fontSize:10, background:'rgba(255,255,255,0.05)', color:'#9CA3AF', display:'flex', alignItems:'center', gap:4 }}
            onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.color='#F9FAFB'; }}
            onMouseLeave={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.05)'; e.currentTarget.style.color='#9CA3AF'; }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
        </div>
      </div>

      {/* Trade levels grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:1, background:'rgba(255,255,255,0.04)', margin:'0 0 0 0' }}>
        {[
          { label:'ENTRY ZONE', value: fmtP(sig.price), color:'#F9FAFB', desc:'Current price' },
          { label:'STOP LOSS',  value: fmtP(sig.stopLoss), color:'#EF4444', desc:'Invalidation' },
          { label:'TARGET 1',   value: fmtP(sig.target1),  color:'#10B981', desc: sig.rr ? `R:R ${sig.rr}×` : 'First target' },
          { label:'TARGET 2',   value: fmtP(sig.target2),  color:'#34d399', desc:'Extended target' },
        ].map(({ label, value, color, desc }) => (
          <div key={label} style={{ padding:'10px 14px', background:'rgba(8,10,15,0.6)' }}>
            <div style={{ fontSize:8, fontWeight:700, color:'#374151', letterSpacing:'.1em', textTransform:'uppercase', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:13, fontWeight:700, color, fontVariantNumeric:'tabular-nums', marginBottom:2 }}>{value}</div>
            <div style={{ fontSize:9, color:'#4B5563' }}>{desc}</div>
          </div>
        ))}
      </div>

      {/* Leverage + confirming signals */}
      <div style={{ padding:'10px 20px 14px', display:'flex', gap:16, flexWrap:'wrap', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontSize:9, fontWeight:600, color:'#374151', letterSpacing:'.08em', textTransform:'uppercase', marginBottom:5 }}>Leverage Suggestion</div>
          <div style={{ fontSize:12, fontWeight:700, color:lvgColor, background:`${lvgColor}15`, border:`1px solid ${lvgColor}35`, padding:'3px 10px', borderRadius:5, display:'inline-block' }}>{sig.leverage}</div>
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:9, fontWeight:600, color:'#374151', letterSpacing:'.08em', textTransform:'uppercase', marginBottom:5 }}>Confirming Signals</div>
          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
            {sig.confirming.map((c, i) => (
              <span key={i} style={{ fontSize:9, padding:'2px 7px', borderRadius:4, background:cfg.bg, border:`1px solid ${cfg.border}`, color:cfg.color }}>{c}</span>
            ))}
          </div>
        </div>
        <div style={{ fontSize:9, color:'#374151', alignSelf:'flex-end', marginLeft:'auto' }}>xrprightnow.com · Not financial advice</div>
      </div>
    </div>
  );
}

function SignalsPanel({ onSelectSignal, activeSignal, chartInstanceRef, symbol, interval, activeIndicators, prices }) {
  const [signals, setSignals]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [genAt, setGenAt]       = useState(null);
  const [sortBy, setSortBy]     = useState('confidence'); // confidence | rr

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/charts/signals');
      setSignals(data.signals);
      setGenAt(data.generated_at);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = signals ? [...signals].sort((a, b) =>
    sortBy === 'rr' ? (b.rr || 0) - (a.rr || 0) : b.confidence - a.confidence
  ) : [];

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Header */}
      <div style={{ padding:'10px 8px 6px', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
          <div style={{ fontSize:9, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em' }}>Signal Scanner</div>
          <button onClick={load} disabled={loading} style={{ fontSize:9, color:'#4B5563', background:'none', border:'none', cursor:'pointer', padding:0 }}>{loading ? '...' : '↻ Refresh'}</button>
        </div>
        <div style={{ display:'flex', gap:3 }}>
          {['confidence','rr'].map(s => (
            <button key={s} onClick={() => setSortBy(s)} style={{ flex:1, fontSize:9, padding:'3px 0', borderRadius:4, border:'none', cursor:'pointer', background: sortBy===s ? 'rgba(14,165,233,0.2)' : 'rgba(255,255,255,0.04)', color: sortBy===s ? '#38BDF8' : '#4B5563', transition:'all 0.15s' }}>
              {s === 'confidence' ? 'By Confidence' : 'By R:R'}
            </button>
          ))}
        </div>
      </div>
      {/* Signal list */}
      <div style={{ flex:1, overflowY:'auto' }}>
        {loading && !signals && <div style={{ padding:'20px', textAlign:'center', fontSize:11, color:'#4B5563' }}>Scanning markets...</div>}
        {sorted.map(sig => (
          <SignalRow
            key={`${sig.symbol}|${sig.interval}`}
            sig={sig}
            onSelect={() => onSelectSignal(sig)}
            active={activeSignal?.symbol===sig.symbol && activeSignal?.interval===sig.interval}
          />
        ))}
        {signals && sorted.length === 0 && <div style={{ padding:'20px', textAlign:'center', fontSize:11, color:'#4B5563' }}>No setups found</div>}
      </div>
      {genAt && <div style={{ padding:'6px 10px', fontSize:9, color:'#374151', borderTop:'1px solid rgba(255,255,255,0.04)' }}>Updated {new Date(genAt).toLocaleTimeString()}</div>}
    </div>
  );
}

function PairBadge({ symbol, active, onClick, price }) {
  const color = PAIR_COLORS[symbol] || '#6B7280';
  const [base, quote] = symbol.split('/');
  const isBtc = quote === 'BTC';
  const sats = isBtc && price ? Math.round(parseFloat(price.close) * 1e8) : null;
  return (
    <button onClick={onClick} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', width:'100%', textAlign:'left', background: active ? 'rgba(255,255,255,0.08)' : 'transparent', border:'none', borderRadius:7, cursor:'pointer', transition:'background 0.15s', flexWrap:'wrap' }}>
      <span style={{ width:7, height:7, borderRadius:'50%', background:color, flexShrink:0 }} />
      <span style={{ fontSize:12, fontWeight: active?600:400, color: active?'#F9FAFB':'#9CA3AF' }}>{base}</span>
      <span style={{ fontSize:10, color:'#4B5563' }}>/{quote}</span>
      {price && <span style={{ fontSize:10, color: price.change >= 0 ? '#10B981':'#EF4444', marginLeft:'auto' }}>{price.change >= 0?'+':''}{parseFloat(price.change).toFixed(1)}%</span>}
    </button>
  );
}

function IndicatorToggle({ ind, active, onClick }) {
  return (
    <button onClick={onClick} title={ind.desc} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', width:'100%', textAlign:'left', background: active ? `${ind.color}18` : 'transparent', border: active ? `1px solid ${ind.color}40` : '1px solid transparent', borderRadius:7, cursor:'pointer', transition:'all 0.15s' }}>
      <span style={{ fontSize:13, lineHeight:1, color: active ? ind.color : '#6B7280' }}>{ind.icon}</span>
      <span style={{ fontSize:11, fontWeight: active?500:400, color: active ? '#F9FAFB' : '#6B7280' }}>{ind.label}</span>
      <span style={{ marginLeft:'auto', width:8, height:8, borderRadius:'50%', background: active ? ind.color : 'transparent', border: active ? 'none' : '1px solid #374151', transition:'all 0.2s', flexShrink:0 }} />
    </button>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} title="Copy to clipboard" style={{ padding:'2px 8px', borderRadius:6, border:'1px solid rgba(255,255,255,0.08)', cursor:'pointer', fontSize:10, background:'rgba(255,255,255,0.04)', color: copied ? '#10B981' : '#6B7280', display:'flex', alignItems:'center', gap:4, transition:'all 0.15s' }}>
      {copied ? (
        <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Copied</>
      ) : (
        <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy</>
      )}
    </button>
  );
}

function WriteupPanel({ symbol, interval, activeIndicators, bundle, divergences = [] }) {
  const CLIENT_ONLY = ['FIB','ELLIOTT','MA200','BREADTH','PATTERNS','SR'];
  const dbTabs = activeIndicators.filter(k => !CLIENT_ONLY.includes(k));
  const defaultTab = dbTabs[0] || activeIndicators[0] || null;
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [writeup, setWriteup]     = useState(null);
  const [wuLoading, setWuLoading] = useState(false);

  // Reset tab whenever pair/interval/indicators change
  useEffect(() => {
    const first = activeIndicators.find(k => !['FIB','ELLIOTT','MA200','BREADTH','PATTERNS','SR'].includes(k)) || activeIndicators[0] || null;
    setActiveTab(first);
    setWriteup(null);
  }, [symbol, interval, activeIndicators.join(',')]);

  // Fetch writeup whenever active tab, symbol, or interval changes
  useEffect(() => {
    if (!activeTab) return;

    if (activeTab === 'FIB' && bundle?.candles?.length && bundle?.symbol === symbol.toUpperCase()) {
      const fibs = calcFibLevels(bundle.candles);
      const keyFibs = fibs.filter(f => [0, 0.382, 0.5, 0.618, 0.786, 1, 1.618].includes(f.ratio));
      setWriteup({ writeup: '📐 Fibonacci Levels — Auto-calculated from recent swing high/low\n\n' +
        keyFibs.map(f => f.label + ': $' + f.price.toFixed(4)).join('\n') +
        '\n\nThe 0.618 golden ratio is the most critical retracement level. Price holding above it signals bullish structure. The 1.618 extension is the primary bull target.',
        generated_at: new Date().toISOString() });
      return;
    }
    if (activeTab === 'ELLIOTT' && bundle?.candles?.length && bundle?.symbol === symbol.toUpperCase()) {
      const { analysis } = calcElliottWave(bundle.candles);
      const trend = analysis?.isUptrend ? 'bullish' : 'bearish';
      const currentWave = analysis?.currentWave || 'unknown';
      setWriteup({ writeup: 'Elliott Wave Analysis\n\nCurrent wave: ' + currentWave + '\nMacro trend: ' + trend.toUpperCase() +
        '\n\nHow to read the wave markers on the chart:\n• Green arrows (①③⑤) = Impulse waves — move WITH the trend\n• Red arrows (②④) = Corrective waves — move AGAINST the trend\n• Orange arrow (A) = Start of corrective ABC sequence\n\nWhat this means now:\n' +
        (analysis?.isUptrend ?
          'Price is in a ' + trend + ' impulse sequence. Wave ③ is typically the strongest and longest wave — if we are approaching or in Wave ③, expect an acceleration higher. Wave ⑤ completes the impulse before a larger ABC correction.' :
          'Price is in a ' + trend + ' corrective sequence. Look for the ABC correction to complete before the next major impulse wave begins. The end of Wave C is often the best buying opportunity in a bull market.') +
        '\n\nNote: Elliott Wave counts are probabilistic, not guaranteed. Always use with other indicators for confirmation.',
        generated_at: new Date().toISOString() });
      return;
    }
    if (activeTab === 'BREADTH' && bundle?.breadthData?.length && bundle?.symbol === symbol.toUpperCase()) {
      const latest = bundle.breadthData[bundle.breadthData.length - 1];
      const pairs = bundle.breadthPairs || [];
      const pct = parseFloat(latest.breadth_pct);
      const above = parseInt(latest.above), total = parseInt(latest.total);
      const ivLabel = { '1day':'daily','1week':'weekly','1month':'monthly' }[interval] || interval;
      const ivLong = { '1day':'daily','1week':'weekly','1month':'monthly' }[interval] || interval;
      const abovePairs = pairs.filter(p => p.above).map(p => p.symbol.split('/')[0]);
      const belowPairs = pairs.filter(p => !p.above).map(p => p.symbol.split('/')[0]);

      let zone, zoneDesc;
      if (pct >= 80) { zone = 'OVERBOUGHT'; zoneDesc = 'Almost every tracked coin is in an uptrend. This is a strong bullish signal but also a warning — when breadth is this high there are few coins left to join the rally, so momentum can stall. Watch for breadth to start rolling over (dropping from 80%+ toward 60%) as an early warning sign before price peaks.'; }
      else if (pct >= 60) { zone = 'BULLISH'; zoneDesc = 'The majority of coins are in uptrends. This is the healthiest zone — broad participation means the rally has real support behind it. Good conditions for holding positions and buying pullbacks.'; }
      else if (pct >= 40) { zone = 'NEUTRAL'; zoneDesc = 'The market is split. Roughly half are in uptrends, half are not. This is a choppy, low-conviction environment — avoid overtrading and wait for a clearer directional signal.'; }
      else if (pct >= 20) { zone = 'BEARISH'; zoneDesc = 'Most coins are in downtrends. Even if your coin looks okay on its own chart, the broad market headwind makes breakouts fail more often. Reduce position size and tighten stops.'; }
      else { zone = 'OVERSOLD'; zoneDesc = 'Extreme broad weakness — nearly every coin is below its EMA. This kind of capitulation has historically been a contrarian buy signal. Don\'t chase shorts here; watch for breadth to start recovering as an early reversal signal.'; }

      let text = `◈ Market Breadth — ${ivLabel.charAt(0).toUpperCase()+ivLabel.slice(1)}\n\n`;
      text += `What this tells you: Instead of just looking at one coin, breadth asks "is the whole crypto market moving together?" It counts how many of the ${total} tracked USD pairs are trading ABOVE their 21-period EMA (the average of the last 21 ${ivLong} closes). When most coins are above their EMA, the market is in a broad uptrend. When most are below, broad weakness is setting in.\n\n`;
      text += `Current reading: ${above} of ${total} pairs above their 21 EMA — ${pct}% — ${zone}\n\n`;
      if (abovePairs.length) text += `✅ Above 21 EMA (uptrend): ${abovePairs.join(', ')}\n`;
      if (belowPairs.length) text += `❌ Below 21 EMA (downtrend): ${belowPairs.join(', ')}\n`;
      text += `\n${zoneDesc}\n\n`;
      text += `How to use it with ${symbol.split('/')[0]}:\n`;
      text += `• ${symbol.split('/')[0]} moving UP + breadth rising = strong, confirmed move\n`;
      text += `• ${symbol.split('/')[0]} moving UP + breadth falling = watch out — ${symbol.split('/')[0]} may be running alone and could reverse\n`;
      text += `• ${symbol.split('/')[0]} moving DOWN + breadth falling = broad market selling, high-risk environment\n`;
      text += `• ${symbol.split('/')[0]} moving DOWN + breadth rising = ${symbol.split('/')[0]}-specific weakness, rest of market is fine`;

      setWriteup({ writeup: text, generated_at: new Date().toISOString() });
      return;
    }
    if (activeTab === 'MA200' && bundle?.ma200_data?.length && bundle?.symbol === symbol.toUpperCase()) {
      const lastPrice = parseFloat(bundle.candles?.[bundle.candles.length-1]?.close || 0);
      const lastMA = parseFloat(bundle.ma200_data[bundle.ma200_data.length-1]?.values?.ema || 0);
      const pct = lastMA ? ((lastPrice - lastMA) / lastMA * 100).toFixed(1) : null;
      setWriteup({ writeup: '━ 200 MA Analysis\n\nCurrent price: $' + lastPrice.toFixed(4) + '\n200 MA level: $' + lastMA.toFixed(4) + (pct ? '\nDistance: ' + pct + '%' : '') +
        '\n\nPrice is ' + (lastPrice > lastMA ? 'ABOVE' : 'BELOW') + ' the 200 MA — ' + (lastPrice > lastMA ? 'bullish macro structure. The 200 MA acts as dynamic support on pullbacks.' : 'bearish macro structure. The 200 MA acts as overhead resistance. A reclaim would signal a potential trend reversal.'),
        generated_at: new Date().toISOString() });
      return;
    }
    if (activeTab === 'MA200' && (!bundle?.ma200_data?.length)) {
      const sym = symbol.split('/')[0];
      const isMonthly = interval === '1month';
      setWriteup({
        writeup: isMonthly
          ? '━ 200 EMA — Monthly Chart\n\nThe 200-period EMA is not available on the monthly timeframe for ' + sym + '.\n\nWhy: A 200-period monthly EMA requires ~17 years of price history. Most crypto assets simply haven\'t been trading that long.\n\nWhat to do instead:\n• Switch to the Daily or Weekly chart to see the 200 EMA\n• On Monthly, use the EMA indicator — the 21-period monthly EMA spans ~2 years and gives a strong trend signal\n• The EMA 50 monthly (50 months ≈ 4 years) is the closest long-term monthly reference available'
          : '━ 200 EMA — Weekly Chart\n\nThe 200-period weekly EMA is not yet available for ' + sym + '.\n\nWhy: ' + sym + ' doesn\'t have enough trading history — a 200-week EMA requires ~4 years of weekly data.\n\nWhat to do instead:\n• Switch to the Daily chart to view the 200 EMA\n• On Weekly, use the EMA indicator for the 21-period weekly average',
        generated_at: new Date().toISOString(),
      });
      return;
    }

    if (activeTab === 'ICHIMOKU' && bundle?.candles?.length >= 52) {
      const ichi = calcIchimoku(bundle.candles, interval);
      if (ichi) {
        const candles = bundle.candles;
        const lastClose = parseFloat(candles[candles.length - 1].close);
        const lastTenkan = ichi.tenkan[ichi.tenkan.length - 1]?.value;
        const lastKijun  = ichi.kijun[ichi.kijun.length - 1]?.value;
        const now = Math.floor(Date.now() / 1000);
        const lastSpanA  = [...ichi.spanA].reverse().find(p => p.time <= now)?.value;
        const lastSpanB  = [...ichi.spanB].reverse().find(p => p.time <= now)?.value;
        const cloudTop = lastSpanA != null && lastSpanB != null ? Math.max(lastSpanA, lastSpanB) : null;
        const cloudBot = lastSpanA != null && lastSpanB != null ? Math.min(lastSpanA, lastSpanB) : null;
        const cloudBull = lastSpanA != null && lastSpanB != null && lastSpanA > lastSpanB;
        const aboveCloud = cloudTop != null && lastClose > cloudTop;
        const insideCloud = cloudTop != null && cloudBot != null && lastClose <= cloudTop && lastClose >= cloudBot;
        const belowCloud = cloudBot != null && lastClose < cloudBot;
        const priceVsTenkan = lastTenkan ? (lastClose > lastTenkan ? 'above' : 'below') : null;
        const priceVsKijun  = lastKijun  ? (lastClose > lastKijun  ? 'above' : 'below') : null;
        const fmt = v => v == null ? '—' : symbol.endsWith('/BTC') ? v.toFixed(8) : '$' + v.toFixed(4);
        let text = '☁ Ichimoku Cloud Analysis\n\n';
        text += `Current price: ${fmt(lastClose)}\n`;
        if (lastTenkan) text += `Tenkan-sen (9): ${fmt(lastTenkan)}\n`;
        if (lastKijun)  text += `Kijun-sen (26): ${fmt(lastKijun)}\n`;
        if (cloudTop)   text += `Cloud: ${fmt(cloudBot)} – ${fmt(cloudTop)} (${cloudBull ? 'green, Span A dominant' : 'red, Span B dominant'})\n`;
        text += '\n';
        if (aboveCloud)       text += `Price is ABOVE the cloud — bullish structure. The cloud acts as dynamic support below.\n`;
        else if (insideCloud) text += `Price is INSIDE the cloud — consolidation / indecision zone. A close above the cloud would be bullish; a close below would be bearish.\n`;
        else if (belowCloud)  text += `Price is BELOW the cloud — bearish structure. The cloud acts as overhead resistance.\n`;
        if (priceVsTenkan) text += `\nPrice is ${priceVsTenkan} the Tenkan-sen — ${priceVsTenkan === 'above' ? 'short-term momentum is bullish.' : 'short-term momentum is bearish.'}`;
        if (priceVsKijun)  text += `\nPrice is ${priceVsKijun} the Kijun-sen — ${priceVsKijun === 'above' ? 'medium-term trend is bullish.' : 'medium-term trend is bearish.'}`;
        text += `\n\nChikou Span traces current price 26 bars back — use it to see how current price compares to past PA at the same point in the cycle.\n`;
        text += `\nThe cloud projects 26 periods into the future — a ${cloudBull ? 'green (bullish)' : 'red (bearish)'} cloud ahead signals the expected directional bias.`;
        setWriteup({ writeup: text, generated_at: new Date().toISOString() });
      }
      return;
    }

    if (activeTab === 'PATTERNS') {
      const tfLabel = { '1day': 'Daily', '1week': 'Weekly', '1month': 'Monthly' }[interval] || 'Daily';
      setWriteup({ writeup: `Wedge Patterns · ${tfLabel}\n\nThis indicator scans ${tfLabel.toLowerCase()} candles for falling wedges, rising wedges, and ascending channels using confirmed pivot highs and lows. Each timeframe is detected independently — switching to Weekly or Monthly re-runs the detection on that timeframe's own OHLC data.\n\nFalling Wedge (bullish): Two descending trendlines converging downward. Price is making lower highs and lower lows, but the support line is falling slower than resistance — the selling is losing momentum. A breakout above the upper trendline is the signal. Measured target shown on confirmed breakout.\n\nRising Wedge (bearish): Two ascending trendlines converging upward. Price makes higher highs and higher lows, but the resistance line is rising slower than support — buying momentum is fading. Requires at least 15% line contraction to qualify. Target shown only after a confirmed close below support.\n\nAscending Channel (neutral): Both trendlines slope upward at similar rates with less than 15% narrowing — not enough contraction to classify as a wedge. Watch for a break above resistance (bullish continuation) or below support (reversal).\n\nDots on the chart mark the exact pivot points the algorithm used. Dashed target line shows the measured move on confirmed breakout or breakdown.`, generated_at: new Date().toISOString() });
      return;
    }

    if (activeTab === 'SR') {
      const allCandles = bundle?.candles;
      if (!allCandles?.length) { setWriteup(null); return; }

      // Same lookback cap as the chart drawing so levels match what's on screen
      const srLookback = { '1day': 120, '1week': 52, '1month': 24 }[interval] || 120;
      const candles = allCandles.slice(-srLookback);

      const now    = new Date();
      const itvMs  = { '1day': 86400000, '1week': 604800000, '1month': 2592000000 }[interval] || 86400000;
      const price  = parseFloat(candles[candles.length - 1].close);
      const dp     = price < 1 ? 4 : 2;
      const fmt    = v => '$' + v.toFixed(dp);

      // ── Pivot detection (W bars each side) ─────────────────────────────
      const W = 4;
      // Each pivot carries the full candle so we can score it later
      const pivotHighCandles = [], pivotLowCandles = [];
      for (let i = W; i < candles.length - W; i++) {
        const c = candles[i];
        let isHigh = true, isLow = true;
        for (let j = i - W; j <= i + W; j++) {
          if (j === i) continue;
          if (parseFloat(candles[j].high) >= parseFloat(c.high)) isHigh = false;
          if (parseFloat(candles[j].low)  <= parseFloat(c.low))  isLow  = false;
        }
        if (isHigh) pivotHighCandles.push({ ...c, idx: i, y: parseFloat(c.high) });
        if (isLow)  pivotLowCandles.push({ ...c, idx: i, y: parseFloat(c.low)  });
      }

      // ── Cluster nearby pivots into one level ────────────────────────────
      function clusterFull(pivots, pct = 0.015) {
        if (!pivots.length) return [];
        const sorted = [...pivots].sort((a, b) => a.y - b.y);
        const groups = [[sorted[0]]];
        for (let i = 1; i < sorted.length; i++) {
          const prev = groups[groups.length - 1];
          const avg  = prev.reduce((a, b) => a + b.y, 0) / prev.length;
          if (Math.abs(sorted[i].y - avg) / avg < pct) prev.push(sorted[i]);
          else groups.push([sorted[i]]);
        }
        return groups.map(g => {
          const level    = g.reduce((a, b) => a + b.y, 0) / g.length;
          const strength = g.length;

          // Rejection score: average wick-to-range ratio at tests
          // For resistance pivots: upper wick = high - max(open,close)
          // For support pivots:  lower wick = min(open,close) - low
          const rejections = g.map(c => {
            const hi = parseFloat(c.high), lo = parseFloat(c.low);
            const op = parseFloat(c.open), cl = parseFloat(c.close);
            const range = hi - lo || 0.0001;
            const bodyTop = Math.max(op, cl), bodyBot = Math.min(op, cl);
            const upperWick = hi - bodyTop;
            const lowerWick = bodyBot - lo;
            // resistance pivot: upper wick dominance; support pivot: lower wick dominance
            return { upperRatio: upperWick / range, lowerRatio: lowerWick / range };
          });
          const avgUpperRej = rejections.reduce((a, r) => a + r.upperRatio, 0) / rejections.length;
          const avgLowerRej = rejections.reduce((a, r) => a + r.lowerRatio, 0) / rejections.length;

          // Most recent test
          const lastCandle = g.reduce((a, b) => (a.idx > b.idx ? a : b));
          const lastDt     = new Date(lastCandle.datetime);
          const barsAgo    = candles.length - 1 - lastCandle.idx;
          const daysAgo    = Math.round((now - lastDt) / itvMs);

          return { level, strength, avgUpperRej, avgLowerRej, barsAgo, daysAgo };
        });
      }

      const resistanceLevels = clusterFull(pivotHighCandles)
        .filter(l => l.level > price)
        .sort((a, b) => a.level - b.level)
        .slice(0, 5);

      const supportLevels = clusterFull(pivotLowCandles)
        .filter(l => l.level < price)
        .sort((a, b) => b.level - a.level)
        .slice(0, 5);

      // ── Format a level line ─────────────────────────────────────────────
      const tfUnit = { '1day': 'day', '1week': 'week', '1month': 'month' }[interval] || 'day';

      function scoreLine(l, isRes) {
        const touchLabel  = l.strength >= 3 ? 'strong' : l.strength === 2 ? 'moderate' : 'weak';
        const rejRatio    = isRes ? l.avgUpperRej : l.avgLowerRej;
        const rejLabel    = rejRatio >= 0.35 ? 'sharp rejection' : rejRatio >= 0.20 ? 'moderate rejection' : 'soft touch';
        const timeLabel   = l.daysAgo === 0 ? 'tested today'
                          : l.daysAgo === 1 ? `tested 1 ${tfUnit} ago`
                          : `tested ${l.daysAgo} ${tfUnit}s ago`;
        return `  ${fmt(l.level)}  |  ${l.strength} touch${l.strength > 1 ? 'es' : ''} · ${touchLabel}  |  ${rejLabel}  |  ${timeLabel}`;
      }

      const resLines = resistanceLevels.length
        ? resistanceLevels.map(l => scoreLine(l, true)).join('\n')
        : '  None detected above current price';
      const supLines = supportLevels.length
        ? supportLevels.map(l => scoreLine(l, false)).join('\n')
        : '  None detected below current price';

      const nearest    = resistanceLevels[0];
      const nearestSup = supportLevels[0];
      const distToRes  = nearest    ? ((nearest.level    - price) / price * 100).toFixed(1) : null;
      const distToSup  = nearestSup ? ((price - nearestSup.level) / price * 100).toFixed(1) : null;

      let summary = `Current price: ${fmt(price)}\n`;
      if (nearest)    summary += `Nearest resistance: ${fmt(nearest.level)} — ${distToRes}% above\n`;
      if (nearestSup) summary += `Nearest support: ${fmt(nearestSup.level)} — ${distToSup}% below`;

      setWriteup({ writeup: `Support & Resistance\n\n${summary}\n\nResistance levels (red):\n${resLines}\n\nSupport levels (green):\n${supLines}\n\nHow to read: touches = how many times price tested the level. Rejection quality (sharp/moderate/soft) = average wick size at the test — a sharp rejection means price hit the level and snapped back hard, which signals strong conviction from buyers or sellers at that price.`, generated_at: new Date().toISOString() });
      return;
    }

    // DB-backed indicator
    setWuLoading(true);
    setWriteup(null);
    apiFetch(`/api/charts/writeup?symbol=${encodeURIComponent(symbol)}&interval=${interval}&indicator=${activeTab}`)
      .then(data => setWriteup(data))
      .catch(() => setWriteup(null))
      .finally(() => setWuLoading(false));
  }, [activeTab, symbol, interval, bundle]);

  return (
    <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ display:'flex', gap:4, padding:'10px 16px 0', flexWrap:'wrap' }}>
        <span style={{ fontSize:11, fontWeight:500, color:'#E0F2FE', marginRight:8, alignSelf:'center' }}>✦ AI Analysis</span>
        {activeIndicators.map(k => {
          const ind = INDICATORS.find(i => i.key === k);
          return (
            <button key={k} onClick={() => setActiveTab(k)} style={{ padding:'3px 10px', borderRadius:20, border:'none', cursor:'pointer', fontSize:11, fontWeight: activeTab===k?500:400, background: activeTab===k ? `${ind?.color}25` : 'transparent', color: activeTab===k ? ind?.color : '#6B7280', transition:'all 0.15s' }}>
              {ind?.label}
            </button>
          );
        })}
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
          {writeup?.generated_at && (
            <span style={{ fontSize:10, color:'#4B5563' }}>
              Updated {formatDistanceToNow(new Date(writeup.generated_at), { addSuffix: true })}
            </span>
          )}
          {writeup?.writeup && (
            <CopyButton text={writeup.writeup} />
          )}
        </div>
      </div>
      {divergences.length > 0 && (
        <div style={{ margin:'0 20px 4px', padding:'10px 14px', borderRadius:8, background:'rgba(0,0,0,0.2)', border:`1px solid ${divergences[0].type === 'bullish' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
          <div style={{ fontSize:10, fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:6 }}>⚡ Divergence Signal{divergences.length > 1 ? 's' : ''} Detected</div>
          {divergences.map((div, i) => {
            const isBull = div.type === 'bullish';
            const ivLabel = { '1day':'daily','1week':'weekly','1month':'monthly' }[interval] || interval;
            return (
              <div key={i} style={{ marginBottom: i < divergences.length-1 ? 6 : 0 }}>
                <span style={{ color: isBull ? '#10B981' : '#EF4444', fontWeight:600, fontSize:12 }}>
                  {isBull ? '▲ Bullish' : '▼ Bearish'} Divergence — {div.indicator}
                </span>
                <div style={{ fontSize:11, color:'#9CA3AF', lineHeight:1.6, marginTop:2 }}>
                  {isBull
                    ? `Price is printing a lower low while ${div.indicator} is holding a higher low on the ${ivLabel} chart. This is a classic bullish divergence — momentum is strengthening even as price dips, often preceding a reversal or bounce.`
                    : `Price is printing a higher high while ${div.indicator} is making a lower high on the ${ivLabel} chart. This is a bearish divergence — momentum is fading as price pushes higher, often a warning sign before a pullback.`
                  }
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ padding:'12px 20px 20px', fontSize:12, color:'#D1D5DB', lineHeight:1.8, whiteSpace:'pre-wrap', maxHeight:320, overflowY:'auto' }}>
        {wuLoading ? <span style={{ color:'#4B5563' }}>Loading analysis...</span> : writeup?.writeup || 'Analysis generating — check back shortly.'}
      </div>
    </div>
  );
}

function calcIchimoku(candles, interval) {
  const n = candles.length;
  if (n < 52) return null;
  const high  = candles.map(c => parseFloat(c.high));
  const low   = candles.map(c => parseFloat(c.low));
  const close = candles.map(c => parseFloat(c.close));
  const times = candles.map(c => Math.floor(new Date(c.datetime).getTime() / 1000));
  const itvSec = { '1day': 86400, '1week': 604800, '1month': 2592000 }[interval] || 86400;

  const maxH = (i, p) => Math.max(...high.slice(Math.max(0, i - p + 1), i + 1));
  const minL = (i, p) => Math.min(...low.slice(Math.max(0, i - p + 1), i + 1));
  const fwdTime = (i, shift) => i + shift < n ? times[i + shift] : times[n - 1] + (i + shift - n + 1) * itvSec;

  const tenkan = [], kijun = [], chikou = [], spanA = [], spanB = [];

  for (let i = 0; i < n; i++) {
    const tk = (maxH(i, 9)  + minL(i, 9))  / 2;
    const kj = (maxH(i, 26) + minL(i, 26)) / 2;
    if (i >= 8)  tenkan.push({ time: times[i], value: tk });
    if (i >= 25) kijun.push({ time: times[i], value: kj });
    // Chikou: today's close plotted 26 bars back
    if (i >= 26) chikou.push({ time: times[i - 26], value: close[i] });
    // Senkou: plotted 26 bars forward
    if (i >= 25) spanA.push({ time: fwdTime(i, 26), value: (tk + kj) / 2 });
    if (i >= 51) spanB.push({ time: fwdTime(i, 26), value: (maxH(i, 52) + minL(i, 52)) / 2 });
  }
  // Deduplicate by time (keep last)
  const dedup = arr => {
    const m = new Map(); arr.forEach(p => m.set(p.time, p)); return [...m.values()].sort((a,b)=>a.time-b.time);
  };
  return { tenkan: dedup(tenkan), kijun: dedup(kijun), chikou: dedup(chikou), spanA: dedup(spanA), spanB: dedup(spanB) };
}

function ChartPanel({ symbol, interval, activeIndicators, bundle, ma200Data, patterns = [], loading, onChartReady, onDivergenceDetected, expanded, currentPrice, drawMode, drawColor, drawWidth, isEraser, onRegisterClear }) {
  const containerRef     = useRef(null);
  const chartRef         = useRef(null);
  const seriesRef        = useRef({});
  const linesRef         = useRef([]);
  const [chartDivergences, setChartDivergences] = useState([]);
  const [divBadgePositions, setDivBadgePositions] = useState([]);

  const computeBadgePositions = useCallback(() => {
    const chart = chartRef.current;
    const candleSeries = seriesRef.current.candle;
    if (!chart || !candleSeries || !chartDivergences.length) { setDivBadgePositions([]); return; }
    // Build a time→high lookup so bullish badges anchor to candle high, not the swing low
    const highByTime = {};
    (bundle?.candles || []).forEach(c => {
      highByTime[Math.floor(new Date(c.datetime).getTime() / 1000)] = parseFloat(c.high);
    });
    const positions = chartDivergences.map(div => {
      try {
        const x = chart.timeScale().timeToCoordinate(div.p2.time);
        // Bearish: p2.value is swing high ✓ | Bullish: p2.value is swing low → use candle high
        const anchorPrice = div.type === 'bullish'
          ? (highByTime[div.p2.time] || div.p2.value)
          : div.p2.value;
        const y = candleSeries.priceToCoordinate(anchorPrice);
        return x !== null && y !== null ? { x, y: y - 30, div } : null;
      } catch { return null; }
    }).filter(Boolean);
    setDivBadgePositions(positions);
  }, [chartDivergences, bundle]);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, { ...getChartTheme(), width: containerRef.current.clientWidth, height: 460, handleScroll: true, handleScale: true });
    chartRef.current = chart;
    if (onChartReady) onChartReady(chart);
    const ro = new ResizeObserver(() => { if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth }); });
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; seriesRef.current = {}; linesRef.current = []; };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !bundle?.candles?.length) return;

    // Remove all existing series
    Object.values(seriesRef.current).forEach(s => { try { chart.removeSeries(s); } catch {} });
    linesRef.current.forEach(l => {
      try { if (seriesRef.current.candle) seriesRef.current.candle.removePriceLine(l); } catch {}
    });
    seriesRef.current = {};
    linesRef.current = [];

    // Remove sub-panes from previous render (pane 0 is always main, remove 1+)
    try {
      const panes = chart.panes();
      for (let i = panes.length - 1; i >= 1; i--) { try { chart.removePane(i); } catch {} }
    } catch {}

    // Resize chart to fill available space without scrolling
    const oscillators = ['RSI','MACD','STOCHRSI','BREADTH'].filter(k => activeIndicators.includes(k));
    let totalH;
    if (expanded) {
      // Fill the full viewport minus the top bar (~57px) and chart header (~60px)
      totalH = Math.max(500, window.innerHeight - 120);
    } else {
      const baseH = 320;
      totalH = baseH + oscillators.length * 130;
    }
    chart.applyOptions({ height: totalH });
    // Give sub-panes a proportional slice so nothing gets cut off
    if (expanded && oscillators.length > 0) {
      const subPaneH = Math.floor(totalH * 0.22);
      try {
        const panes = chart.panes();
        panes.forEach((pane, i) => { if (i > 0) { try { pane.setHeight(subPaneH); } catch {} } });
      } catch {}
    }

    const candles = bundle.candles;
    const candleData = candles.map(c => ({
      time: Math.floor(new Date(c.datetime).getTime()/1000),
      open: parseFloat(c.open), high: parseFloat(c.high),
      low:  parseFloat(c.low),  close: parseFloat(c.close),
      volume: parseFloat(c.volume) || 0,
    })).sort((a,b)=>a.time-b.time);

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor:'#10B981', downColor:'#EF4444', borderUpColor:'#10B981', borderDownColor:'#EF4444', wickUpColor:'#10B981', wickDownColor:'#EF4444',
      lastValueVisible: false, priceLineVisible: false,
    }, 0);
    candleSeries.setData(candleData);
    seriesRef.current.candle = candleSeries;

    // Volume bars — semi-transparent, in main pane on its own scale
    const hasVolume = candleData.some(c => c.volume > 0);
    if (hasVolume) {
      const volSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume',
        lastValueVisible: false, priceLineVisible: false,
      }, 0);
      chart.priceScale('volume').applyOptions({ scaleMarginTop: 0.8, scaleMarginBottom: 0, borderVisible: false });
      volSeries.setData(candleData.map(c => ({
        time: c.time,
        value: c.volume,
        color: c.close >= c.open ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)',
      })));
      seriesRef.current.vol = volSeries;
    }

    const indData = bundle.indicator_data || [];
    const toLine = (key) => indData.map(d => ({ time: Math.floor(new Date(d.datetime).getTime()/1000), value: parseFloat(d.values[key]) })).filter(d => !isNaN(d.value)).sort((a,b)=>a.time-b.time);

    if (activeIndicators.includes('BBANDS') && indData.length) {
      const upper = chart.addSeries(LineSeries, { color:'rgba(96,165,250,0.4)', lineWidth:1, priceLineVisible:false, lastValueVisible:false }, 0);
      const mid   = chart.addSeries(LineSeries, { color:'#60a5fa', lineWidth:1.5, priceLineVisible:false, lastValueVisible:false }, 0);
      const lower = chart.addSeries(LineSeries, { color:'rgba(96,165,250,0.4)', lineWidth:1, priceLineVisible:false, lastValueVisible:false }, 0);
      upper.setData(toLine('upper')); mid.setData(toLine('mid')); lower.setData(toLine('lower'));
      seriesRef.current.bbU = upper; seriesRef.current.bbM = mid; seriesRef.current.bbL = lower;
    }

    if (activeIndicators.includes('EMA') && indData.length) {
      const ema21 = chart.addSeries(LineSeries, { color:'#a78bfa', lineWidth:1.5, priceLineVisible:false, lastValueVisible:false, title:'EMA 21' }, 0);
      ema21.setData(toLine('ema'));
      seriesRef.current.ema = ema21;
      if (bundle.ema50_data?.length) {
        const ema50pts = bundle.ema50_data.map(d => ({ time: Math.floor(new Date(d.datetime).getTime()/1000), value: parseFloat(d.values.ema) })).filter(d => !isNaN(d.value)).sort((a,b) => a.time-b.time);
        const ema50 = chart.addSeries(LineSeries, { color:'#f59e0b', lineWidth:1.5, priceLineVisible:false, lastValueVisible:false, title:'EMA 50' }, 0);
        ema50.setData(ema50pts);
        seriesRef.current.ema50 = ema50;
      }
    }

    if (activeIndicators.includes('THREE_RULE') && bundle.three_rule_data?.length) {
      const toThreeLine = (field) => bundle.three_rule_data
        .map(d => ({ time: Math.floor(new Date(d.datetime).getTime()/1000), value: parseFloat(d.values[field]) }))
        .filter(d => !isNaN(d.value) && d.value > 0)
        .sort((a,b) => a.time-b.time);
      const tr8  = chart.addSeries(LineSeries, { color:'#22c55e', lineWidth:1.5, priceLineVisible:false, lastValueVisible:false, title:'8 EMA'  }, 0);
      const tr21 = chart.addSeries(LineSeries, { color:'#a78bfa', lineWidth:1.5, priceLineVisible:false, lastValueVisible:false, title:'21 EMA' }, 0);
      const tr50 = chart.addSeries(LineSeries, { color:'#f59e0b', lineWidth:2,   priceLineVisible:false, lastValueVisible:false, title:'50 SMA' }, 0);
      tr8.setData(toThreeLine('ema8'));
      tr21.setData(toThreeLine('ema21'));
      tr50.setData(toThreeLine('sma50'));
      seriesRef.current.tr8 = tr8; seriesRef.current.tr21 = tr21; seriesRef.current.tr50 = tr50;
    }

    if (activeIndicators.includes('MA200') && bundle.ma200_data?.length) {
      const ma200Series = chart.addSeries(LineSeries, { color:'rgba(255,255,255,0.9)', lineWidth:2, priceLineVisible:false, lastValueVisible:true, crosshairMarkerVisible:false, title:'MA200' }, 0);
      const ma200Line = bundle.ma200_data.map(d => ({ time: Math.floor(new Date(d.datetime).getTime()/1000), value: parseFloat(d.values.ema) })).filter(d => !isNaN(d.value) && d.value > 0).sort((a,b) => a.time-b.time);
      if (ma200Line.length) ma200Series.setData(ma200Line);
      seriesRef.current.ma200 = ma200Series;
    }

    // Oscillators — each gets its own sub-pane
    let paneIdx = 1;
    const divLookback = { '1day': 5, '1week': 3, '1month': 2 }[interval] || 5;
    const allDivergences = [];

    const drawDivergenceLines = (divergences, indSeries, pane) => {
      divergences.forEach(div => {
        const color = div.type === 'bullish' ? 'rgba(16,185,129,0.8)' : 'rgba(239,68,68,0.8)';
        // Price trendline on main pane
        try {
          const priceLine = chart.addSeries(LineSeries, { color, lineWidth:2, lineStyle:LineStyle.Dashed, priceLineVisible:false, lastValueVisible:false, crosshairMarkerVisible:false }, 0);
          priceLine.setData([
            { time: div.p1.time, value: div.p1.value },
            { time: div.p2.time, value: div.p2.value },
          ]);
          seriesRef.current[`divPrice_${div.type}_${div.p1.time}`] = priceLine;
        } catch {}
        // Indicator trendline on oscillator pane
        try {
          const indLine = chart.addSeries(LineSeries, { color, lineWidth:2, lineStyle:LineStyle.Dashed, priceLineVisible:false, lastValueVisible:false, crosshairMarkerVisible:false }, pane);
          indLine.setData([
            { time: div.p1.time, value: div.ind1 },
            { time: div.p2.time, value: div.ind2 },
          ]);
          seriesRef.current[`divInd_${div.type}_${div.p1.time}`] = indLine;
        } catch {}
      });
    };

    if (activeIndicators.includes('RSI') && indData.length) {
      try { chart.addPane(); } catch {}
      const rsiData = toLine('rsi');
      const rsi = chart.addSeries(LineSeries, { color:'#a78bfa', lineWidth:1.5, priceLineVisible:false, lastValueVisible:true, title:'RSI' }, paneIdx);
      rsi.setData(rsiData);
      rsi.createPriceLine({ price:70, color:'rgba(239,68,68,0.5)', lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:true, title:'70' });
      rsi.createPriceLine({ price:50, color:'rgba(156,163,175,0.3)', lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:false });
      rsi.createPriceLine({ price:30, color:'rgba(16,185,129,0.5)', lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:true, title:'30' });
      seriesRef.current.rsi = rsi;
      const rsiDivs = detectDivergences(candleData, rsiData, divLookback);
      rsiDivs.forEach(d => allDivergences.push({ ...d, indicator: 'RSI' }));
      if (rsiDivs.length) drawDivergenceLines(rsiDivs, rsi, paneIdx);
      paneIdx++;
    }

    if (activeIndicators.includes('MACD') && indData.length) {
      try { chart.addPane(); } catch {}
      // Histogram (coloured bars behind the lines)
      const macdVals = toLine('macd');
      const signalVals = toLine('signal');
      const histData = macdVals.map(m => {
        const sig = signalVals.find(s => s.time === m.time);
        const diff = sig ? m.value - sig.value : 0;
        return { time: m.time, value: diff, color: diff >= 0 ? 'rgba(52,211,153,0.4)' : 'rgba(248,113,113,0.4)' };
      });
      const hist = chart.addSeries(HistogramSeries, { priceLineVisible:false, lastValueVisible:false }, paneIdx);
      hist.setData(histData);
      const macdLine   = chart.addSeries(LineSeries, { color:'#34d399', lineWidth:1.5, priceLineVisible:false, lastValueVisible:true, title:'MACD' }, paneIdx);
      const signalLine = chart.addSeries(LineSeries, { color:'#f87171', lineWidth:1, priceLineVisible:false, lastValueVisible:false, title:'Signal' }, paneIdx);
      macdLine.setData(macdVals); signalLine.setData(signalVals);
      macdLine.createPriceLine({ price:0, color:'rgba(156,163,175,0.3)', lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:false });
      seriesRef.current.hist = hist; seriesRef.current.macd = macdLine; seriesRef.current.signal = signalLine;
      const macdDivs = detectDivergences(candleData, macdVals, divLookback);
      macdDivs.forEach(d => allDivergences.push({ ...d, indicator: 'MACD' }));
      if (macdDivs.length) drawDivergenceLines(macdDivs, macdLine, paneIdx);
      paneIdx++;
    }

    if (activeIndicators.includes('STOCHRSI') && indData.length) {
      try { chart.addPane(); } catch {}
      const kData = toLine('k');
      const k = chart.addSeries(LineSeries, { color:'#38bdf8', lineWidth:1.5, priceLineVisible:false, lastValueVisible:true, title:'%K' }, paneIdx);
      const d = chart.addSeries(LineSeries, { color:'#f472b6', lineWidth:1, priceLineVisible:false, lastValueVisible:false, title:'%D' }, paneIdx);
      k.setData(kData); d.setData(toLine('d'));
      k.createPriceLine({ price:80, color:'rgba(239,68,68,0.5)', lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:true, title:'80' });
      k.createPriceLine({ price:20, color:'rgba(16,185,129,0.5)', lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:true, title:'20' });
      seriesRef.current.k = k; seriesRef.current.d = d;
      const stochDivs = detectDivergences(candleData, kData, divLookback);
      stochDivs.forEach(d => allDivergences.push({ ...d, indicator: 'StochRSI' }));
      if (stochDivs.length) drawDivergenceLines(stochDivs, k, paneIdx);
      paneIdx++;
    }

    if (activeIndicators.includes('BREADTH') && bundle.breadthData?.length) {
      try { chart.addPane(); } catch {}
      const breadthLine = bundle.breadthData.map(d => ({
        time: Math.floor(new Date(d.datetime).getTime() / 1000),
        value: parseFloat(d.breadth_pct),
      })).filter(d => !isNaN(d.value)).sort((a,b) => a.time - b.time);

      // Colour-coded histogram behind the line
      const breadthHist = chart.addSeries(HistogramSeries, { priceLineVisible:false, lastValueVisible:false }, paneIdx);
      breadthHist.setData(breadthLine.map(d => ({
        time: d.time, value: d.value,
        color: d.value >= 60 ? 'rgba(16,185,129,0.25)' : d.value >= 40 ? 'rgba(251,191,36,0.2)' : 'rgba(239,68,68,0.25)',
      })));
      const breadthSeries = chart.addSeries(LineSeries, { color:'#fb923c', lineWidth:2, priceLineVisible:false, lastValueVisible:true, title:'Breadth %' }, paneIdx);
      breadthSeries.setData(breadthLine);
      breadthSeries.createPriceLine({ price:80, color:'rgba(239,68,68,0.5)',   lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:true, title:'80' });
      breadthSeries.createPriceLine({ price:50, color:'rgba(156,163,175,0.35)', lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:false });
      breadthSeries.createPriceLine({ price:20, color:'rgba(16,185,129,0.5)',  lineWidth:1, lineStyle:LineStyle.Dashed, axisLabelVisible:true, title:'20' });
      seriesRef.current.breadth = breadthSeries; seriesRef.current.breadthH = breadthHist;
      paneIdx++;
    }

    if (activeIndicators.includes('ICHIMOKU')) {
      const ichi = calcIchimoku(candles, interval);
      if (ichi) {
        // Tenkan-sen (orange)
        const tenkanS = chart.addSeries(LineSeries, { color:'#F97316', lineWidth:1, priceLineVisible:false, lastValueVisible:false, title:'Tenkan' }, 0);
        tenkanS.setData(ichi.tenkan);
        seriesRef.current.tenkan = tenkanS;
        // Kijun-sen (blue)
        const kijunS = chart.addSeries(LineSeries, { color:'#3B82F6', lineWidth:1.5, priceLineVisible:false, lastValueVisible:false, title:'Kijun' }, 0);
        kijunS.setData(ichi.kijun);
        seriesRef.current.kijun = kijunS;
        // Chikou span (lime) - plotted 26 bars back
        const chikouS = chart.addSeries(LineSeries, { color:'#A3E635', lineWidth:1, priceLineVisible:false, lastValueVisible:false, title:'Chikou' }, 0);
        chikouS.setData(ichi.chikou);
        seriesRef.current.chikou = chikouS;
        // Senkou Span A (green area — fills down creating upper cloud boundary)
        const spanAS = chart.addSeries(AreaSeries, { lineColor:'rgba(16,185,129,0.8)', topColor:'rgba(16,185,129,0.12)', bottomColor:'rgba(16,185,129,0.02)', lineWidth:1, priceLineVisible:false, lastValueVisible:false, title:'Span A', crosshairMarkerVisible:false }, 0);
        spanAS.setData(ichi.spanA);
        seriesRef.current.spanA = spanAS;
        // Senkou Span B (red area — fills down creating lower cloud boundary)
        const spanBS = chart.addSeries(AreaSeries, { lineColor:'rgba(239,68,68,0.8)', topColor:'rgba(239,68,68,0.12)', bottomColor:'rgba(239,68,68,0.02)', lineWidth:1, priceLineVisible:false, lastValueVisible:false, title:'Span B', crosshairMarkerVisible:false }, 0);
        spanBS.setData(ichi.spanB);
        seriesRef.current.spanB = spanBS;
      }
    }

    if (activeIndicators.includes('FIB')) {
      const fibs = calcFibLevels(candles);
      fibs.forEach(fib => {
        const line = candleSeries.createPriceLine({
          price: fib.price,
          color: fib.color,
          lineWidth: [0.618, 0, 1].includes(fib.ratio) ? 2 : 1,
          lineStyle: fib.ratio > 1 ? LineStyle.Dashed : LineStyle.Solid,
          axisLabelVisible: true,
          title: fib.label,
        });
        linesRef.current.push(line);
      });
    }

    if (activeIndicators.includes('ELLIOTT')) {
      const { markers } = calcElliottWave(candles);
      markers.forEach(m => {
        try {
          const matchCandle = candles.find(c => Math.floor(new Date(c.datetime).getTime()/1000) === m.time);
          if (!matchCandle) return;
          const price = parseFloat(matchCandle[m.position === 'belowBar' ? 'low' : 'high']);
          const line = candleSeries.createPriceLine({
            price, color: m.color, lineWidth: 1,
            lineStyle: LineStyle.Dotted,
            axisLabelVisible: true, title: m.text,
          });
          linesRef.current.push(line);
        } catch(e) {}
      });
    }


    setChartDivergences(allDivergences);
    if (onDivergenceDetected) onDivergenceDetected(allDivergences);

    // Show a clean default window — scroll back for more history
    const now = Math.floor(Date.now() / 1000);
    const rightOffset = { '1day': 12, '1week': 3, '1month': 1 }[interval] ?? 12;
    try {
      const lookback = { '1day': 120 * 86400, '1week': 40 * 7 * 86400, '1month': 24 * 30 * 86400 }[interval] || 120 * 86400;
      chart.timeScale().setVisibleRange({ from: now - lookback, to: now + 2 * 86400 });
      chart.timeScale().applyOptions({ rightOffset });
    } catch { chart.timeScale().fitContent(); }
  }, [bundle, symbol, activeIndicators, ma200Data, expanded]);

  // Sats axis label — price line with line hidden, only axis label visible
  const satsPriceLineRef = useRef(null);
  useEffect(() => {
    const candleSeries = seriesRef.current.candle;
    if (!candleSeries) return;
    if (satsPriceLineRef.current) {
      try { candleSeries.removePriceLine(satsPriceLineRef.current); } catch {}
      satsPriceLineRef.current = null;
    }
    if (symbol.endsWith('/BTC') && currentPrice) {
      const sats = Math.round(parseFloat(currentPrice.close) * 1e8);
      satsPriceLineRef.current = candleSeries.createPriceLine({
        price: parseFloat(currentPrice.close),
        color: 'transparent',
        lineVisible: false,
        lineWidth: 1,
        axisLabelVisible: true,
        axisLabelColor: '#06B6D4',
        axisLabelTextColor: '#000000',
        title: `${sats.toLocaleString()} sats`,
      });
    }
  }, [symbol, currentPrice, bundle]);

  // Draw detected patterns (wedge trendlines)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !patterns.length) return;

    const patternSeries = [];
    patterns.forEach(pat => {
      const isBull    = pat.bias === 'bullish';
      const isChannel = pat.type === 'ASCENDING_CHANNEL';
      // Falling wedge: red resistance, green support
      // Rising wedge: red both (bearish)
      // Ascending channel: blue/cyan both (neutral)
      const resColor = isChannel ? 'rgba(56,189,248,0.85)' : 'rgba(239,68,68,0.85)';
      const supColor = isChannel ? 'rgba(56,189,248,0.65)' : isBull ? 'rgba(16,185,129,0.85)' : 'rgba(239,68,68,0.6)';

      const resSeries = chart.addSeries(LineSeries, {
        color: resColor, lineWidth: 2, lineStyle: LineStyle.Dashed,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        title: pat.label,
      }, 0);
      resSeries.setData(pat.resistanceLine.filter(p => p.value > 0));
      patternSeries.push(resSeries);

      const supSeries = chart.addSeries(LineSeries, {
        color: supColor, lineWidth: 2, lineStyle: LineStyle.Dashed,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
      }, 0);
      supSeries.setData(pat.supportLine.filter(p => p.value > 0));
      patternSeries.push(supSeries);

      // Confirmed target (breakdown/breakout already happened)
      if (pat.target) {
        resSeries.createPriceLine({
          price: pat.target,
          color: isBull ? '#10B981' : '#EF4444',
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: `${pat.label} Target`,
        });
      } else if (pat.potentialTarget) {
        // Pre-confirmation: show dotted potential target with muted color + label
        resSeries.createPriceLine({
          price: pat.potentialTarget,
          color: isBull ? 'rgba(16,185,129,0.45)' : 'rgba(239,68,68,0.45)',
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: `Potential Target`,
        });
      }

      // Draw small dots at anchor pivot points — each pivot gets a tiny horizontal tick via a 2-point line
      if (pat.anchorHighs?.length) {
        pat.anchorHighs.forEach(p => {
          if (!p.value || !p.time) return;
          const dot = chart.addSeries(LineSeries, {
            color: resColor, lineWidth: 3,
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
          }, 0);
          dot.setData([{ time: p.time, value: p.value }]);
          patternSeries.push(dot);
        });
      }
      if (pat.anchorLows?.length) {
        pat.anchorLows.forEach(p => {
          if (!p.value || !p.time) return;
          const dot = chart.addSeries(LineSeries, {
            color: supColor, lineWidth: 3,
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
          }, 0);
          dot.setData([{ time: p.time, value: p.value }]);
          patternSeries.push(dot);
        });
      }
    });

    return () => { patternSeries.forEach(s => { try { chart.removeSeries(s); } catch {} }); };
  }, [patterns]);

  // Draw Support & Resistance levels
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !activeIndicators.includes('SR')) return;
    const allCandles = bundle?.candles;
    if (!allCandles || allCandles.length < 20) return;

    // Cap to a timeframe-appropriate lookback so levels match the visible chart
    const srLookback = { '1day': 120, '1week': 52, '1month': 24 }[interval] || 120;
    const candles = allCandles.slice(-srLookback);

    const price = candles[candles.length - 1].close;
    const toTime = c => Math.floor(new Date(c.datetime).getTime() / 1000);

    // Find pivot highs and lows (window of 4 candles each side)
    const W = 4;
    const pivotHighs = [];
    const pivotLows  = [];
    for (let i = W; i < candles.length - W; i++) {
      const c = candles[i];
      let isHigh = true, isLow = true;
      for (let j = i - W; j <= i + W; j++) {
        if (j === i) continue;
        if (candles[j].high >= c.high) isHigh = false;
        if (candles[j].low  <= c.low)  isLow  = false;
      }
      if (isHigh) pivotHighs.push(c.high);
      if (isLow)  pivotLows.push(c.low);
    }

    // Cluster nearby levels — merge within 1.5% of each other
    function cluster(levels, pct = 0.015) {
      if (!levels.length) return [];
      const sorted = [...levels].sort((a, b) => a - b);
      const groups = [[sorted[0]]];
      for (let i = 1; i < sorted.length; i++) {
        const prev = groups[groups.length - 1];
        const avg  = prev.reduce((a, b) => a + b, 0) / prev.length;
        if (Math.abs(sorted[i] - avg) / avg < pct) {
          prev.push(sorted[i]);
        } else {
          groups.push([sorted[i]]);
        }
      }
      // Return avg of each cluster with touch count as strength
      return groups.map(g => ({
        level:    g.reduce((a, b) => a + b, 0) / g.length,
        strength: g.length,
      })).sort((a, b) => b.strength - a.strength);
    }

    const resistanceLevels = cluster(pivotHighs)
      .filter(l => l.level > price)
      .slice(0, 5);

    const supportLevels = cluster(pivotLows)
      .filter(l => l.level < price)
      .slice(-5);

    const srLines = [];

    // Draw resistance lines (red)
    resistanceLevels.forEach(({ level, strength }) => {
      const series = chart.addSeries(LineSeries, {
        color: 'rgba(239,68,68,0.0)', lineWidth: 1,
        priceLineVisible: false, lastValueVisible: false,
        crosshairMarkerVisible: false,
      }, 0);
      series.setData([
        { time: toTime(candles[0]), value: level },
        { time: toTime(candles[candles.length - 1]), value: level },
      ]);
      series.createPriceLine({
        price: level,
        color: strength >= 3 ? '#EF4444' : 'rgba(239,68,68,0.6)',
        lineWidth: strength >= 3 ? 2 : 1,
        lineStyle: strength >= 3 ? LineStyle.Solid : LineStyle.Dashed,
        axisLabelVisible: true,
        title: strength >= 3 ? 'R' : 'r',
      });
      srLines.push(series);
    });

    // Draw support lines (green)
    supportLevels.forEach(({ level, strength }) => {
      const series = chart.addSeries(LineSeries, {
        color: 'rgba(16,185,129,0.0)', lineWidth: 1,
        priceLineVisible: false, lastValueVisible: false,
        crosshairMarkerVisible: false,
      }, 0);
      series.setData([
        { time: toTime(candles[0]), value: level },
        { time: toTime(candles[candles.length - 1]), value: level },
      ]);
      series.createPriceLine({
        price: level,
        color: strength >= 3 ? '#10B981' : 'rgba(16,185,129,0.6)',
        lineWidth: strength >= 3 ? 2 : 1,
        lineStyle: strength >= 3 ? LineStyle.Solid : LineStyle.Dashed,
        axisLabelVisible: true,
        title: strength >= 3 ? 'S' : 's',
      });
      srLines.push(series);
    });

    return () => { srLines.forEach(s => { try { chart.removeSeries(s); } catch {} }); };
  }, [activeIndicators, bundle]);

  // Recompute badge positions whenever divergences or chart scroll/zoom changes
  useEffect(() => {
    computeBadgePositions();
    const chart = chartRef.current;
    if (!chart) return;
    chart.timeScale().subscribeVisibleLogicalRangeChange(computeBadgePositions);
    return () => { try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(computeBadgePositions); } catch {} };
  }, [computeBadgePositions]);

  // Drawing canvas
  const drawCanvasRef = useRef(null);
  const isDrawing     = useRef(false);
  const lastPos       = useRef(null);

  // Resize canvas to match chart container
  useEffect(() => {
    const canvas = drawCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ro = new ResizeObserver(() => {
      const { width, height } = container.getBoundingClientRect();
      canvas.width  = width;
      canvas.height = height;
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0] || e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    if (!drawMode) return;
    e.preventDefault();
    isDrawing.current = true;
    lastPos.current = getPos(e, drawCanvasRef.current);
  }, [drawMode]);

  const draw = useCallback((e) => {
    if (!drawMode || !isDrawing.current) return;
    e.preventDefault();
    const canvas = drawCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    ctx.strokeStyle = drawColor;
    ctx.lineWidth   = isEraser ? drawWidth * 5 : drawWidth;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }, [drawMode, drawColor, drawWidth, isEraser]);

  const stopDraw = useCallback(() => { isDrawing.current = false; }, []);

  const clearCanvas = useCallback(() => {
    const canvas = drawCanvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => { if (onRegisterClear) onRegisterClear(clearCanvas); }, [clearCanvas, onRegisterClear]);

  return (
    <div style={{ position:'relative' }}>
      {loading && (
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(10,12,18,0.7)', borderRadius:12, zIndex:5 }}>
          <div style={{ color:'#38BDF8', fontSize:12 }}>Loading chart...</div>
        </div>
      )}
      {divBadgePositions.map((pos, i) => {
        const bull = pos.div.type === 'bullish';
        return (
          <div key={i} style={{
            position:'absolute', zIndex:4,
            left: pos.x, top: pos.y,
            transform: 'translateX(-50%)',
            display:'flex', alignItems:'center', gap:4,
            padding:'4px 10px', borderRadius:6, fontSize:10, fontWeight:700,
            background: bull ? '#065f46' : '#7f1d1d',
            border: `1px solid ${bull ? '#10B981' : '#EF4444'}`,
            color: bull ? '#6EE7B7' : '#FCA5A5',
            boxShadow: `0 2px 8px ${bull ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            letterSpacing: '0.02em',
          }}>
            {bull ? '▲' : '▼'} {pos.div.indicator} {bull ? 'Bull' : 'Bear'} Div
          </div>
        );
      })}
      <div ref={containerRef} style={{ width:'100%', borderRadius:12, overflow:'hidden' }} />

      {/* Drawing canvas overlay */}
      <canvas
        ref={drawCanvasRef}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
        style={{ position:'absolute', inset:0, borderRadius:12, zIndex:6,
          cursor: drawMode ? (isEraser ? 'cell' : 'crosshair') : 'default',
          pointerEvents: drawMode ? 'all' : 'none',
        }}
      />

    </div>
  );
}

function PaywallPanel() {
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setLoginError(data.error || 'Login failed'); return; }
      localStorage.setItem('token', data.token);
      window.location.reload();
    } catch {
      setLoginError('Something went wrong. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:400, flexDirection:'column', gap:14, padding:30 }}>
      <div style={{ fontSize:16, fontWeight:600 }}>Pro Chart Room</div>
      <div style={{ fontSize:13, color:'#6B7280', textAlign:'center', maxWidth:300, lineHeight:1.6 }}>Full charts, all indicators, AI analysis — updated automatically.</div>
      <div style={{ display:'flex', gap:10, marginTop:6, flexWrap:'wrap', justifyContent:'center' }}>
        <button onClick={() => window.location.href='/connect/patreon'} style={{ padding:'9px 16px', borderRadius:8, border:'none', cursor:'pointer', background:'#F96854', color:'#fff', fontSize:13, fontWeight:500 }}>Connect Patreon ($25+ tier)</button>
        <button onClick={async () => {
          const email = prompt('Enter your email to subscribe:');
          if (!email) return;
          try {
            const res = await fetch('/api/stripe/chartroom-checkout', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ email }) });
            const data = await res.json();
            if (data.url) window.location.href = data.url;
          } catch { alert('Error starting checkout. Please try again.'); }
        }} style={{ padding:'9px 16px', borderRadius:8, cursor:'pointer', background:'transparent', border:'1px solid rgba(255,255,255,0.15)', color:'#D1D5DB', fontSize:13 }}>Subscribe $10/mo</button>
      </div>

      {!showLogin ? (
        <button onClick={() => setShowLogin(true)} style={{ marginTop:4, background:'none', border:'none', color:'#38BDF8', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
          Already have an account? Sign in
        </button>
      ) : (
        <form onSubmit={handleLogin} style={{ marginTop:8, display:'flex', flexDirection:'column', gap:8, width:'100%', maxWidth:280 }}>
          <input
            type="email" placeholder="Email" required value={loginEmail}
            onChange={e => setLoginEmail(e.target.value)}
            style={{ padding:'9px 12px', borderRadius:6, border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.05)', color:'#F9FAFB', fontSize:13, outline:'none' }}
          />
          <input
            type="password" placeholder="Password" required value={loginPassword}
            onChange={e => setLoginPassword(e.target.value)}
            style={{ padding:'9px 12px', borderRadius:6, border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.05)', color:'#F9FAFB', fontSize:13, outline:'none' }}
          />
          {loginError && <div style={{ fontSize:12, color:'#FCA5A5' }}>{loginError}</div>}
          <button type="submit" disabled={loginLoading} style={{ padding:'9px', borderRadius:6, border:'none', background:'#38BDF8', color:'#0F172A', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {loginLoading ? 'Signing in...' : 'Sign in'}
          </button>
          <button type="button" onClick={() => setShowLogin(false)} style={{ background:'none', border:'none', color:'#6B7280', fontSize:12, cursor:'pointer' }}>Cancel</button>
        </form>
      )}

      <div style={{ marginTop:6, padding:'10px 16px', background:'rgba(249,104,84,0.06)', border:'1px solid rgba(249,104,84,0.15)', borderRadius:8, fontSize:12, color:'#9CA3AF', textAlign:'center', maxWidth:320, lineHeight:1.6 }}>
        Already a Patreon member but can't get in? Click "Connect Patreon" above — this re-links your account and restores access immediately.
      </div>
    </div>
  );
}

export default function ChartRoom() {
  const { user } = useAuth();

  // Capture token from Patreon OAuth redirect and store it
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const access   = params.get('access');
    const err      = params.get('error');
    if (urlToken && access === 'granted') {
      localStorage.setItem('token', urlToken);
      window.history.replaceState({}, '', window.location.pathname);
      window.location.reload();
    } else if (err === 'patreon_no_email') {
      window.history.replaceState({}, '', window.location.pathname);
      alert('Patreon did not share your email address with us.\n\nTo fix this:\n1. Go to patreon.com and open your Account Settings\n2. Find your email address and set visibility to "Patrons only" or "Public"\n3. Come back here and click Connect Patreon again\n\nIf you need help, message us on Patreon.');
    } else if (err === 'patreon_failed') {
      window.history.replaceState({}, '', window.location.pathname);
      alert('Something went wrong connecting your Patreon. Please try again or contact support.');
    } else if (access === 'denied') {
      window.history.replaceState({}, '', window.location.pathname);
      alert('Your Patreon pledge does not meet the $25+ tier requirement for Chart Room access.');
    }
  }, []);

  const isPro = user?.subscription_tier === 'premium' || user?.subscription_tier === 'elite' || user?.chart_room_access === true || user?.patreon_tier >= 1;
  const isOrphanAccount = user?.email?.includes('@patreon.local');

  // Drawing tool state (lifted so toolbar can live in header)
  const [drawMode, setDrawMode]   = useState(false);
  const [drawColor, setDrawColor] = useState('#EF4444');
  const [drawWidth, setDrawWidth] = useState(3);
  const [isEraser, setIsEraser]   = useState(false);
  const clearCanvasRef            = useRef(null); // ChartPanel sets this to its clearCanvas fn

  const DRAW_COLORS = ['#EF4444','#F59E0B','#FFFFFF','#10B981','#38BDF8','#A78BFA','#F472B6'];

  const [symbol, setSymbol]               = useState('XRP/USD');
  const [interval, setInterval]           = useState('1week');
  const [activeIndicators, setActiveIndicators] = useState(['BBANDS']);
  const [bundle, setBundle]               = useState(null);
  const [ma200Data, setMa200Data]         = useState([]);
  const [patterns, setPatterns]           = useState([]);
  const [loading, setLoading]             = useState(false);
  const [prices, setPrices]               = useState({});
  const [error, setError]                 = useState(null);
  const [divergences, setDivergences]     = useState([]);
  const [chartExpanded, setChartExpanded] = useState(false);
  const [sidebarMode, setSidebarMode]     = useState('charts'); // 'charts' | 'signals'
  const [activeSignal, setActiveSignal]   = useState(null);
  const chartInstanceRef = useRef(null);

  // Clear drawing when switching charts or indicators
  useEffect(() => {
    setDrawMode(false);
    setIsEraser(false);
    if (clearCanvasRef.current) clearCanvasRef.current();
  }, [symbol, activeIndicators.join(','), interval]);

  const handleSelectSignal = useCallback((sig) => {
    setActiveSignal(sig);
    setSymbol(sig.symbol);
    setInterval(sig.interval);
    // Turn on indicators that are confirming
    const inds = [];
    if (sig.confirming.some(c => c.includes('RSI'))) inds.push('RSI');
    if (sig.confirming.some(c => c.includes('MACD'))) inds.push('MACD');
    if (sig.confirming.some(c => c.includes('BB'))) inds.push('BBANDS');
    if (sig.confirming.some(c => c.includes('Three Rule'))) inds.push('THREE_RULE');
    if (sig.confirming.some(c => c.includes('21 EMA'))) inds.push('EMA');
    if (sig.confirming.some(c => c.includes('200 EMA'))) inds.push('MA200');
    if (sig.confirming.some(c => c.includes('StochRSI'))) inds.push('STOCHRSI');
    if (inds.length > 0) setActiveIndicators([...new Set(inds)]);
    else setActiveIndicators(['BBANDS']);
  }, []);

  const exportTradeCard = useCallback(() => {
    const sig = activeSignal;
    if (!sig) return;
    const rawChart = chartInstanceRef.current?.takeScreenshot();
    const cfg = BIAS_CONFIG[sig.strength] || BIAS_CONFIG['Bullish'];
    const isBtc = sig.symbol.endsWith('/BTC');
    const fmtP = v => v == null ? 'N/A' : isBtc ? v.toFixed(8) : v >= 1000 ? `$${v.toLocaleString(undefined,{maximumFractionDigits:0})}` : v >= 1 ? `$${v.toFixed(4)}` : `$${v.toFixed(6)}`;
    const sf = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

    // ── Fix canvas to a consistent 1400px wide ────────────────────
    const W      = 1400;
    const HEADER = 80;
    const PANEL  = 200;
    const FOOTER = 44;

    // Scale chart to fit W, preserve aspect ratio
    let scaledChart = null, chartH = 0;
    if (rawChart) {
      const scale = W / rawChart.width;
      chartH = Math.round(rawChart.height * scale);
      const tmp = document.createElement('canvas');
      tmp.width = W; tmp.height = chartH;
      tmp.getContext('2d').drawImage(rawChart, 0, 0, W, chartH);
      scaledChart = tmp;
    }

    const H = HEADER + chartH + PANEL + FOOTER;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');

    // ── Background ────────────────────────────────────────────────
    ctx.fillStyle = '#080A0F'; ctx.fillRect(0, 0, W, H);

    // ── Header accent ─────────────────────────────────────────────
    ctx.fillStyle = cfg.color + '18'; ctx.fillRect(0, 0, W, HEADER);
    ctx.fillStyle = cfg.color; ctx.fillRect(0, 0, W, 4);

    // ── Symbol + price ────────────────────────────────────────────
    const currentPriceVal = prices[sig.symbol];
    ctx.font = `bold 32px ${sf}`; ctx.fillStyle = '#F9FAFB';
    ctx.fillText(sig.symbol, 28, 48);
    if (currentPriceVal) {
      const priceStr = isBtc ? parseFloat(currentPriceVal.close).toFixed(8) : `$${parseFloat(currentPriceVal.close).toFixed(4)}`;
      const symW = ctx.measureText(sig.symbol).width;
      ctx.font = `bold 28px ${sf}`; ctx.fillStyle = '#9CA3AF';
      ctx.fillText(priceStr, 28 + symW + 16, 48);
    }
    ctx.font = `500 15px ${sf}`; ctx.fillStyle = '#4B5563';
    ctx.fillText(`${sig.intervalLabel} Chart  ·  Signal Scanner  ·  charts.xrprightnow.com`, 28, 70);

    // ── Confidence pill (top right) ───────────────────────────────
    const pillTxt = `${cfg.icon} ${sig.strength.toUpperCase()}   ${sig.confidence}% CONFIDENCE`;
    ctx.font = `bold 15px ${sf}`;
    const pillW = ctx.measureText(pillTxt).width + 36;
    ctx.fillStyle = cfg.color + '22';
    roundRect(ctx, W - pillW - 24, 20, pillW, 40, 20);
    ctx.fillStyle = cfg.color;
    ctx.fillText(pillTxt, W - pillW - 8, 45);

    // ── Chart ─────────────────────────────────────────────────────
    if (scaledChart) ctx.drawImage(scaledChart, 0, HEADER);

    // ── Accent divider ────────────────────────────────────────────
    ctx.fillStyle = cfg.color + '40'; ctx.fillRect(0, HEADER + chartH, W, 3);

    // ── Trade data panel ──────────────────────────────────────────
    const py = HEADER + chartH + 3;
    ctx.fillStyle = '#0D1017'; ctx.fillRect(0, py, W, PANEL);

    const cells = [
      { label:'ENTRY ZONE', value: fmtP(sig.price),    color:'#F9FAFB', sub:'Current price' },
      { label:'STOP LOSS',  value: fmtP(sig.stopLoss), color:'#EF4444', sub:'Invalidation' },
      { label:'TARGET 1',   value: fmtP(sig.target1),  color:'#10B981', sub: sig.rr ? `R:R ${sig.rr}×` : 'First target' },
      { label:'TARGET 2',   value: fmtP(sig.target2),  color:'#34d399', sub:'Extended target' },
    ];
    const cellW = Math.floor(W * 0.56 / 4);
    const cellH = PANEL - 20;
    cells.forEach(({ label, value, color, sub }, i) => {
      const cx = 18 + i * (cellW + 6);
      ctx.fillStyle = '#141720'; ctx.fillRect(cx, py + 10, cellW, cellH);
      ctx.font = `700 11px ${sf}`; ctx.fillStyle = '#4B5563';
      ctx.fillText(label, cx + 14, py + 34);
      ctx.font = `bold 26px ${sf}`; ctx.fillStyle = color;
      ctx.fillText(value, cx + 14, py + 80);
      ctx.font = `500 12px ${sf}`; ctx.fillStyle = '#374151';
      ctx.fillText(sub, cx + 14, py + 104);
    });

    // ── R:R + Leverage ────────────────────────────────────────────
    const rx = 18 + 4 * (cellW + 6) + 14;
    const rrW = 160;
    ctx.fillStyle = '#141720'; ctx.fillRect(rx, py + 10, rrW, (cellH / 2) - 4);
    ctx.font = `700 11px ${sf}`; ctx.fillStyle = '#4B5563';
    ctx.fillText('R:R RATIO', rx + 14, py + 34);
    ctx.font = `bold 22px ${sf}`; ctx.fillStyle = '#F59E0B';
    ctx.fillText(sig.rr ? `${sig.rr}×` : '—', rx + 14, py + 68);

    const lvgColor = sig.leverage === 'Spot only' ? '#6B7280' : sig.leverage === 'Up to 1.5x' ? '#F59E0B' : sig.leverage === 'Up to 2x' ? '#fb923c' : '#10B981';
    const lvgY = py + 10 + (cellH / 2) + 4;
    ctx.fillStyle = '#141720'; ctx.fillRect(rx, lvgY, rrW, (cellH / 2) - 4);
    ctx.font = `700 11px ${sf}`; ctx.fillStyle = '#4B5563';
    ctx.fillText('LEVERAGE', rx + 14, lvgY + 24);
    ctx.font = `bold 18px ${sf}`; ctx.fillStyle = lvgColor;
    ctx.fillText(sig.leverage, rx + 14, lvgY + 56);

    // ── Confirming signals ────────────────────────────────────────
    const bx = rx + rrW + 16;
    ctx.font = `700 11px ${sf}`; ctx.fillStyle = '#4B5563';
    ctx.fillText('CONFIRMING SIGNALS', bx, py + 34);
    let badgeX = bx, badgeRow = 0;
    sig.confirming.forEach(conf => {
      ctx.font = `500 13px ${sf}`;
      const bw = ctx.measureText(conf).width + 22;
      if (badgeX + bw > W - 24) { badgeX = bx; badgeRow++; }
      const by = py + 44 + badgeRow * 30;
      ctx.fillStyle = cfg.color + '18'; ctx.fillRect(badgeX, by, bw, 22);
      ctx.fillStyle = cfg.color; ctx.fillText(conf, badgeX + 11, by + 15);
      badgeX += bw + 8;
    });

    // ── Footer ────────────────────────────────────────────────────
    const fy = py + PANEL;
    ctx.fillStyle = '#080A0F'; ctx.fillRect(0, fy, W, FOOTER);
    ctx.font = `bold 16px ${sf}`; ctx.fillStyle = '#FFFFFF';
    ctx.fillText('charts.xrprightnow.com', 24, fy + 28);
    ctx.font = `500 13px ${sf}`; ctx.fillStyle = '#374151';
    ctx.fillText('Not financial advice · ' + new Date().toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}), 270, fy + 28);

    const a = document.createElement('a');
    a.download = `${sig.symbol.replace('/','_')}-${sig.interval}-signal.png`;
    a.href = c.toDataURL('image/png'); a.click();
  }, [activeSignal, prices]);

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath(); ctx.moveTo(x+r,y); ctx.lineTo(x+w-r,y);
    ctx.arcTo(x+w,y,x+w,y+r,r); ctx.lineTo(x+w,y+h-r);
    ctx.arcTo(x+w,y+h,x+w-r,y+h,r); ctx.lineTo(x+r,y+h);
    ctx.arcTo(x,y+h,x,y+h-r,r); ctx.lineTo(x,y+r);
    ctx.arcTo(x,y,x+r,y,r); ctx.closePath(); ctx.fill();
  }

  const exportChart = useCallback(() => {
    const chart = chartInstanceRef.current;
    if (!chart) return;
    try {
      const chartCanvas = chart.takeScreenshot();
      // Use chart's native pixel size — no scaling, no blur
      const HEADER = 70, FOOTER = 48;
      const W = chartCanvas.width, H = chartCanvas.height + HEADER + FOOTER;
      const out = document.createElement('canvas');
      out.width = W; out.height = H;
      const ctx = out.getContext('2d');

      // Background
      ctx.fillStyle = '#080A0F';
      ctx.fillRect(0, 0, W, H);

      // Chart image 1:1 — no stretch
      ctx.drawImage(chartCanvas, 0, HEADER);

      // Top bar
      ctx.fillStyle = 'rgba(8,10,15,0.92)';
      ctx.fillRect(0, 0, W, HEADER);

      // Accent dot
      const currentPrice = prices[symbol];
      ctx.beginPath(); ctx.arc(32, HEADER / 2, 6, 0, Math.PI * 2);
      ctx.fillStyle = '#0EA5E9'; ctx.fill();

      // Build top-line text segments then draw left-to-right with proper spacing
      const boldFont = 'bold 24px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      const smallFont = '500 14px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      let x = 48;
      const y = 38;

      ctx.font = boldFont;
      ctx.fillStyle = '#F9FAFB';
      ctx.fillText(symbol, x, y);
      x += ctx.measureText(symbol).width + 14;

      if (currentPrice) {
        const priceStr = '$' + parseFloat(currentPrice.close).toFixed(symbol.endsWith('/BTC') ? 8 : 4);
        ctx.font = boldFont;
        ctx.fillStyle = '#F9FAFB';
        ctx.fillText(priceStr, x, y);
        x += ctx.measureText(priceStr).width + 10;

        const chg = parseFloat(currentPrice.change);
        const chgStr = (chg >= 0 ? '+' : '') + chg.toFixed(2) + '%';
        ctx.font = smallFont;
        ctx.fillStyle = chg >= 0 ? '#10B981' : '#EF4444';
        // vertically centre the smaller text relative to bold line
        ctx.fillText(chgStr, x, y - 1);
      }

      // Sub-label
      const indLabel = activeIndicators.map(k => INDICATORS.find(i => i.key === k)?.label).join(' · ');
      const ivLabel = INTERVALS.find(i => i.key === interval)?.label || interval;
      ctx.fillStyle = '#6B7280';
      ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillText(indLabel + ' · ' + ivLabel, 48, 57);

      // Bottom watermark bar
      ctx.fillStyle = 'rgba(8,10,15,0.92)';
      ctx.fillRect(0, H - FOOTER, W, FOOTER);
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillText('charts.xrprightnow.com', 24, H - 16);
      ctx.fillStyle = '#374151';
      ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillText('Not financial advice · ' + new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), W - 310, H - 16);

      // Download
      const a = document.createElement('a');
      a.download = symbol.replace('/', '-') + '-' + interval + '-' + activeIndicators.join('-') + '.png';
      a.href = out.toDataURL('image/png');
      a.click();
    } catch (e) { console.error('Export failed', e); }
  }, [symbol, interval, activeIndicators, prices]);

  useEffect(() => {
    PAIRS.forEach(async ({ symbol: s }) => {
      try { const d = await apiFetch(`/api/charts/price?symbol=${encodeURIComponent(s)}`); setPrices(p => ({ ...p, [s]: d })); } catch {}
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!isPro) return;
    setLoading(true); setError(null);
    try {
      const primaryInd = activeIndicators.find(k => !['FIB','ELLIOTT','MA200','BREADTH','PATTERNS','SR'].includes(k)) || 'BBANDS';
      const data = await apiFetch(`/api/charts/bundle/${encodeURIComponent(symbol)}/${interval}/${primaryInd}`);
      if (activeIndicators.includes('MA200')) {
        try {
          const ma200Res = await apiFetch(`/api/charts/indicator/${encodeURIComponent(symbol)}/${interval}/EMA_200`);
          data.ma200_data = ma200Res.data || [];
        } catch(e) { data.ma200_data = []; }
      }
      if (activeIndicators.includes('EMA')) {
        try {
          const ema50Res = await apiFetch(`/api/charts/indicator/${encodeURIComponent(symbol)}/${interval}/EMA_50`);
          data.ema50_data = ema50Res.data || [];
        } catch(e) { data.ema50_data = []; }
      }
      if (activeIndicators.includes('THREE_RULE')) {
        try {
          const threeRes = await apiFetch(`/api/charts/indicator/${encodeURIComponent(symbol)}/${interval}/THREE_RULE`);
          data.three_rule_data = threeRes.data || [];
        } catch(e) { data.three_rule_data = []; }
      }
      if (activeIndicators.includes('BREADTH')) {
        try {
          const breadthRes = await apiFetch(`/api/charts/breadth/${interval}`);
          data.breadthData = breadthRes.data || [];
          data.breadthPairs = breadthRes.pairStatus || [];
        } catch(e) { data.breadthData = []; data.breadthPairs = []; }
      }
      setBundle(Object.assign({}, data));
      // Fetch patterns only when PATTERNS indicator is active
      if (activeIndicators.includes('PATTERNS')) {
        try {
          const patRes = await apiFetch(`/api/charts/patterns/${encodeURIComponent(symbol)}/${interval}`);
          setPatterns(patRes.patterns || []);
        } catch { setPatterns([]); }
      } else {
        setPatterns([]);
      }
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [symbol, interval, activeIndicators, isPro]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh chart data every 5 minutes (matches CoinGecko cache TTL)
  useEffect(() => {
    const id = window.setInterval(() => { fetchData(); }, 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [fetchData]);

  const toggleIndicator = (key, multi = false) => {
    setActiveIndicators(prev => {
      if (multi) {
        return prev.includes(key) ? (prev.length > 1 ? prev.filter(k => k !== key) : prev) : [...prev, key];
      }
      return prev.includes(key) && prev.length === 1 ? prev : [key];
    });
  };

  const currentPrice = prices[symbol];
  const changeColor  = parseFloat(currentPrice?.change) >= 0 ? '#10B981' : '#EF4444';
  const accentColor  = PAIR_COLORS[symbol] || '#0EA5E9';

  const usdPairs = PAIRS.filter(p => p.group === 'USD Pairs');
  const btcPairs = PAIRS.filter(p => p.group === 'BTC Pairs');
  const mcapPairs = PAIRS.filter(p => p.group === 'Alts');

  return (
    <div style={{ minHeight:'100vh', background:'#080A0F', color:'#F9FAFB', fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {isOrphanAccount && (
        <div style={{ background:'#92400E', borderBottom:'1px solid #B45309', padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, color:'#FEF3C7' }}>
            Your account needs to be re-linked. Click the button to reconnect your Patreon and restore full access.
          </span>
          <button
            onClick={() => window.location.href = '/connect/patreon'}
            style={{ padding:'6px 14px', borderRadius:6, border:'none', cursor:'pointer', background:'#F59E0B', color:'#1C1917', fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}
          >
            Reconnect Patreon
          </button>
        </div>
      )}
      <style>{`
        @keyframes pulse { 0%,100%{opacity:.3} 50%{opacity:1} }
        ::-webkit-scrollbar { width:3px; } ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.08); border-radius:4px; }
        button:focus { outline:none; }
        @media (max-width: 768px) {
          .cr-desktop-sidebar { display: none !important; }
          .cr-mobile-bar { display: flex !important; }
          .cr-main { height: auto !important; }
          .cr-content { overflow: visible !important; }
        }
        @media (min-width: 769px) {
          .cr-mobile-bar { display: none !important; }
          .cr-desktop-sidebar { display: flex !important; }
          .cr-main { height: calc(100vh - 57px) !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom:'1px solid rgba(255,255,255,0.05)', padding:'10px 14px', display:'flex', alignItems:'center', gap:12 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:600, color:'#F9FAFB' }}>Chart Room</div>
          <div style={{ fontSize:10, color:'#4B5563' }}>Live technical analysis · Auto-updated</div>
        </div>
        {!isPro && <div style={{ marginLeft:'auto', background:'rgba(14,165,233,0.1)', border:'1px solid rgba(14,165,233,0.2)', borderRadius:20, padding:'3px 12px', fontSize:11, color:'#38BDF8' }}>✦ Pro</div>}
      </div>

      {/* Mobile top bar — pairs + indicators as horizontal scrollable strips */}
      <div className="cr-mobile-bar" style={{ display:'none', flexDirection:'column', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
        {/* Pairs strip */}
        <div style={{ overflowX:'auto', display:'flex', gap:6, padding:'8px 10px', scrollbarWidth:'none' }}>
          {[...usdPairs, ...btcPairs, ...mcapPairs].map(({ symbol: s }) => (
            <button key={s} onClick={() => setSymbol(s)} style={{ flexShrink:0, padding:'5px 10px', borderRadius:16, border:'none', cursor:'pointer', fontSize:11, fontWeight:500, background: symbol===s ? accentColor : 'rgba(255,255,255,0.06)', color: symbol===s ? '#fff' : '#9CA3AF', transition:'all 0.15s' }}>
              {s.split('/')[0]}
              {prices[s] && <span style={{ marginLeft:4, fontSize:10, opacity:0.7 }}>${parseFloat(prices[s].close).toFixed(s.endsWith('/BTC')?6:2)}</span>}
            </button>
          ))}
        </div>
        {/* Indicators strip */}
        <div style={{ overflowX:'auto', display:'flex', gap:5, padding:'4px 10px 8px', scrollbarWidth:'none' }}>
          {INDICATORS.map(ind => (
            <button key={ind.key} onClick={(e) => toggleIndicator(ind.key, e.ctrlKey || e.metaKey)} style={{ flexShrink:0, padding:'4px 10px', borderRadius:14, border: activeIndicators.includes(ind.key) ? `1px solid ${ind.color}60` : '1px solid rgba(255,255,255,0.08)', cursor:'pointer', fontSize:10, background: activeIndicators.includes(ind.key) ? `${ind.color}18` : 'rgba(255,255,255,0.03)', color: activeIndicators.includes(ind.key) ? ind.color : '#6B7280', transition:'all 0.15s' }}>
              {ind.label}
            </button>
          ))}
        </div>
      </div>

      <div className="cr-main" style={{ display:'flex' }}>

        {/* Desktop sidebar */}
        <div className="cr-desktop-sidebar" style={{ width:175, flexShrink:0, borderRight:'1px solid rgba(255,255,255,0.05)', flexDirection:'column', overflowY:'auto' }}>
          {/* Sidebar mode toggle */}
          <div style={{ display:'flex', gap:2, padding:'8px 8px 0', flexShrink:0 }}>
            {[['charts','Charts'],['signals','Signals']].map(([mode, label]) => (
              <button key={mode} onClick={() => setSidebarMode(mode)} style={{ flex:1, fontSize:10, fontWeight:500, padding:'5px 0', borderRadius:6, border:'none', cursor:'pointer', background: sidebarMode===mode ? (mode==='signals'?'rgba(16,185,129,0.2)':'rgba(14,165,233,0.2)') : 'rgba(255,255,255,0.04)', color: sidebarMode===mode ? (mode==='signals'?'#34d399':'#38BDF8') : '#4B5563', transition:'all 0.15s' }}>
                {label}
              </button>
            ))}
          </div>

          {sidebarMode === 'signals' ? (
            <SignalsPanel onSelectSignal={handleSelectSignal} activeSignal={activeSignal} chartInstanceRef={chartInstanceRef} symbol={symbol} interval={interval} activeIndicators={activeIndicators} prices={prices} />
          ) : (
          <>
          <div style={{ padding:'10px 8px 4px' }}>
            <div style={{ fontSize:9, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', padding:'0 4px', marginBottom:4 }}>USD Pairs</div>
            {usdPairs.map(({ symbol: s }) => <PairBadge key={s} symbol={s} active={symbol===s} onClick={() => setSymbol(s)} price={prices[s]} />)}
          </div>
          <div style={{ height:1, background:'rgba(255,255,255,0.05)', margin:'6px 10px' }} />
          <div style={{ padding:'4px 8px' }}>
            <div style={{ fontSize:9, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', padding:'0 4px', marginBottom:4 }}>BTC Pairs</div>
            {btcPairs.map(({ symbol: s }) => <PairBadge key={s} symbol={s} active={symbol===s} onClick={() => setSymbol(s)} price={prices[s]} />)}
          </div>
          <div style={{ height:1, background:'rgba(255,255,255,0.05)', margin:'6px 10px' }} />
          <div style={{ padding:'4px 8px' }}>
            <div style={{ fontSize:9, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', padding:'0 4px', marginBottom:4 }}>Alts</div>
            {mcapPairs.map(({ symbol: s }) => <PairBadge key={s} symbol={s} active={symbol===s} onClick={() => setSymbol(s)} price={prices[s]} />)}
          </div>
          <div style={{ height:1, background:'rgba(255,255,255,0.05)', margin:'6px 10px' }} />
          <div style={{ padding:'4px 8px 10px' }}>
            <div style={{ fontSize:9, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', padding:'0 4px', marginBottom:4 }}>Indicators</div>
            {INDICATORS.map(ind => <IndicatorToggle key={ind.key} ind={ind} active={activeIndicators.includes(ind.key)} onClick={(e) => toggleIndicator(ind.key, e.ctrlKey || e.metaKey)} />)}
          </div>
          <div style={{ marginTop:'auto', padding:'10px', borderTop:'1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ fontSize:9, color:'#374151', marginBottom:3 }}>Auto-refresh</div>
            <div style={{ fontSize:10, color:'#4B5563', lineHeight:1.7 }}>Daily · 1:00 AM<br/>Weekly · Mon 2AM<br/>Monthly · 1st 3AM</div>
          </div>
          </>
          )}
        </div>

        {/* Main content */}
        <div className="cr-content" style={{ flex:1, overflow:'auto' }}>
          <div style={{ padding:'12px 14px' }}>
            {/* Symbol + price header */}
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                  <span style={{ width:8, height:8, borderRadius:'50%', background:accentColor, display:'inline-block', flexShrink:0 }} />
                  <span style={{ fontSize:16, fontWeight:600 }}>{symbol}</span>
                  {currentPrice && <>
                    <span style={{ fontSize:16, fontWeight:600 }}>${parseFloat(currentPrice.close).toFixed(symbol.endsWith('/BTC') ? 8 : 4)}</span>
                    {symbol.endsWith('/BTC') && <span style={{ fontSize:12, color:'#6B7280', fontWeight:400 }}>({Math.round(parseFloat(currentPrice.close) * 1e8).toLocaleString()} sats)</span>}
                    <span style={{ fontSize:12, color:changeColor, fontWeight:500 }}>{parseFloat(currentPrice.change)>=0?'+':''}{parseFloat(currentPrice.change).toFixed(2)}%</span>
                  </>}
                </div>
                <div style={{ fontSize:10, color:'#4B5563', marginTop:2 }}>
                  {activeIndicators.map(k => INDICATORS.find(i=>i.key===k)?.label).join(' · ')} · {INTERVALS.find(i=>i.key===interval)?.label}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0, flexWrap:'wrap' }}>
                {/* Drawing toolbar */}
                <div style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 6px', borderRadius:8, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)' }}>
                  <button onClick={() => { setDrawMode(v => !v); setIsEraser(false); }} title="Draw" style={{ width:26, height:26, borderRadius:6, border: drawMode && !isEraser ? '1px solid #38BDF8' : '1px solid transparent', background: drawMode && !isEraser ? 'rgba(56,189,248,0.15)' : 'transparent', color: drawMode && !isEraser ? '#38BDF8' : '#6B7280', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center' }}>✏️</button>
                  {drawMode && <>
                    <button onClick={() => { setIsEraser(v => !v); }} title="Eraser" style={{ width:26, height:26, borderRadius:6, border: isEraser ? '1px solid #F59E0B' : '1px solid transparent', background: isEraser ? 'rgba(245,158,11,0.15)' : 'transparent', color: isEraser ? '#F59E0B' : '#6B7280', cursor:'pointer', fontSize:11, display:'flex', alignItems:'center', justifyContent:'center' }}>⬜</button>
                    <div style={{ width:1, height:14, background:'rgba(255,255,255,0.1)' }} />
                    {DRAW_COLORS.map(c => (
                      <button key={c} onClick={() => { setDrawColor(c); setIsEraser(false); }} style={{ width:14, height:14, borderRadius:'50%', background:c, border: drawColor===c && !isEraser ? '2px solid #fff' : '2px solid transparent', cursor:'pointer', padding:0, flexShrink:0 }} />
                    ))}
                    <div style={{ width:1, height:14, background:'rgba(255,255,255,0.1)' }} />
                    {[2,4,7].map(w => (
                      <button key={w} onClick={() => setDrawWidth(w)} style={{ width:26, height:26, borderRadius:6, border: drawWidth===w ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent', background: drawWidth===w ? 'rgba(255,255,255,0.08)' : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <div style={{ width: w===2?8:w===4?12:16, height:w, borderRadius:2, background:'#9CA3AF' }} />
                      </button>
                    ))}
                    <div style={{ width:1, height:14, background:'rgba(255,255,255,0.1)' }} />
                    <button onClick={() => clearCanvasRef.current?.()} title="Clear" style={{ width:26, height:26, borderRadius:6, border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.08)', color:'#EF4444', cursor:'pointer', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
                  </>}
                </div>
                <div style={{ display:'flex', gap:2, background:'rgba(255,255,255,0.03)', borderRadius:8, padding:3 }}>
                  {INTERVALS.map(iv => (
                    <button key={iv.key} onClick={() => setInterval(iv.key)} style={{ padding:'4px 10px', borderRadius:6, border:'none', cursor:'pointer', fontSize:11, fontWeight: interval===iv.key?500:400, background: interval===iv.key?'rgba(14,165,233,0.2)':'transparent', color: interval===iv.key?'#38BDF8':'#6B7280', transition:'all 0.15s' }}>
                      {iv.short}
                    </button>
                  ))}
                </div>
                <button onClick={exportChart} title="Export chart image" style={{ padding:'4px 8px', borderRadius:8, border:'1px solid rgba(255,255,255,0.08)', cursor:'pointer', fontSize:11, background:'rgba(255,255,255,0.04)', color:'#9CA3AF', display:'flex', alignItems:'center', gap:4, transition:'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.color='#F9FAFB'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.04)'; e.currentTarget.style.color='#9CA3AF'; }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Export
                </button>
                <button onClick={() => setChartExpanded(v => !v)} title={chartExpanded ? 'Collapse chart' : 'Expand chart (OBS mode)'} style={{ padding:'4px 8px', borderRadius:8, border:`1px solid ${chartExpanded ? 'rgba(14,165,233,0.4)' : 'rgba(255,255,255,0.08)'}`, cursor:'pointer', fontSize:11, background: chartExpanded ? 'rgba(14,165,233,0.12)' : 'rgba(255,255,255,0.04)', color: chartExpanded ? '#38BDF8' : '#9CA3AF', display:'flex', alignItems:'center', gap:4, transition:'all 0.15s' }}>
                  {chartExpanded ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                  )}
                  {chartExpanded ? 'Collapse' : 'Expand'}
                </button>
              </div>
            </div>

            <div style={{ background:'rgba(255,255,255,0.02)', borderRadius:12, border:'1px solid rgba(255,255,255,0.05)', overflow:'hidden', position:'relative' }}>
              {isPro ? (
                <>
                  <div style={{ padding:'10px 10px 0' }}>
                    {!loading && bundle?.symbol === symbol && bundle?.interval === interval && !bundle.candles?.length ? (
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300, flexDirection:'column', gap:10 }}>
                        <div style={{ fontSize:24 }}>📊</div>
                        <div style={{ fontSize:13, fontWeight:500, color:'#9CA3AF' }}>No {INTERVALS.find(i=>i.key===interval)?.label} data yet for {symbol}</div>
                        <div style={{ fontSize:11, color:'#4B5563' }}>Data refreshes automatically overnight — try Daily interval</div>
                      </div>
                    ) : (
                      <ChartPanel symbol={symbol} interval={interval} activeIndicators={activeIndicators} bundle={bundle} ma200Data={ma200Data} patterns={patterns} loading={loading} onChartReady={chart => { chartInstanceRef.current = chart; }} onDivergenceDetected={setDivergences} expanded={chartExpanded} currentPrice={currentPrice} drawMode={drawMode} drawColor={drawColor} drawWidth={drawWidth} isEraser={isEraser} onRegisterClear={fn => { clearCanvasRef.current = fn; }} />
                    )}
                  </div>
                  {!chartExpanded && (
                    activeSignal && activeSignal.symbol === symbol && activeSignal.interval === interval
                      ? <TradeCard sig={activeSignal} onExport={exportTradeCard} />
                      : <WriteupPanel symbol={symbol} interval={interval} activeIndicators={activeIndicators} bundle={bundle} divergences={divergences} />
                  )}
                </>
              ) : (
                <PaywallPanel />
              )}
            </div>

            {error && <div style={{ marginTop:10, padding:'8px 12px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:8, fontSize:12, color:'#FCA5A5' }}>{error}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
